import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import constant from '../constants/constant';
import { 
  Building2, Users, UserCheck, GitMerge, Shield, Search, Plus, 
  ArrowRightLeft, CheckCircle2, XCircle, RefreshCw, Award, 
  Calendar, DollarSign, Clock, MoreVertical, Edit2, Trash2, Eye,
  CheckCircle, AlertCircle, Globe, Video, TrendingUp, Sparkles, X, ExternalLink, Filter
} from 'lucide-react';
import { agenciesAPI, usersAPI } from '../utils/api';
import { SearchableCountrySelect, FilterCountrySelect } from '../components/SearchableCountrySelect';

const OrganizationHub = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('adminToken');
  const userinfo = useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';
  const isSuperAdmin = userType === 'SUPER_ADMIN' || userType === 'ADMIN' || userType === 'OWNER';
  const isOwner = userType === 'OWNER';
  const isManager = userType === 'MANAGER';

  // Active sub-tab (Default to managers for Owner/Super Admin, bds for Manager)
  const [activeTab, setActiveTab] = useState(isManager ? 'bds' : 'managers'); // 'managers' | 'bds' | 'agencies' | 'hosts'
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);

  // Cross-hierarchy filter state (Manager -> BDs, BD -> Agencies)
  const [selectedManagerFilter, setSelectedManagerFilter] = useState(null);
  const [selectedBdFilter, setSelectedBdFilter] = useState(null);

  // Robust hierarchy relation check helpers
  const isBdUnderManager = (bd, manager) => {
    if (!bd || !manager) return false;
    const mgrKeys = [manager.id, manager.user_id, manager.xoxo_id].filter(Boolean).map(k => String(k).trim().toLowerCase());
    const bdMgrId = bd.manager_id ? String(bd.manager_id).trim().toLowerCase() : '';
    return Boolean(bdMgrId && mgrKeys.includes(bdMgrId));
  };

  const isAgencyUnderBd = (agency, bd) => {
    if (!agency || !bd) return false;
    const bdKeys = [bd.id, bd.user_id, bd.xoxo_id].filter(Boolean).map(k => String(k).trim().toLowerCase());
    const agBdUser = agency.bd_user_id ? String(agency.bd_user_id).trim().toLowerCase() : '';
    const agBdCode = agency.bd_code ? String(agency.bd_code).trim().toLowerCase() : '';
    return Boolean((agBdUser && bdKeys.includes(agBdUser)) || (agBdCode && bdKeys.includes(agBdCode)));
  };

  // Country flags helper
  const countryFlags = {
    India: '🇮🇳',
    USA: '🇺🇸',
    'United States': '🇺🇸',
    UAE: '🇦🇪',
    'United Arab Emirates': '🇦🇪',
    Pakistan: '🇵🇰',
    Bangladesh: '🇧🇩',
    'Saudi Arabia': '🇸🇦',
    UK: '🇬🇧',
    'United Kingdom': '🇬🇧',
    Canada: '🇨🇦',
    Australia: '🇦🇺',
    Nepal: '🇳🇵',
    Kuwait: '🇰🇼',
    Qatar: '🇶🇦',
    Oman: '🇴🇲',
    Philippines: '🇵🇭',
    Indonesia: '🇮🇩'
  };
  const getFlag = (country) => countryFlags[country] || '🌐';

  const commonCountries = [
    "India", "United States", "United Arab Emirates", "Saudi Arabia", 
    "United Kingdom", "Pakistan", "Bangladesh", "Canada", "Australia", 
    "Nepal", "Kuwait", "Qatar", "Oman", "Philippines", "Indonesia"
  ];

  // Data states
  const [managers, setManagers] = useState([]);
  const [bds, setBds] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [hosts, setHosts] = useState([]);

  // Toast notifications
  const [toast, setToast] = useState({ message: '', type: 'success', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type, visible: false }), 3500);
  };

  // Modals
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const [isBdModalOpen, setIsBdModalOpen] = useState(false);
  const [isAgencyModalOpen, setIsAgencyModalOpen] = useState(false);
  const [isHostModalOpen, setIsHostModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [selectedAgencyForTransfer, setSelectedAgencyForTransfer] = useState(null);
  const [transferTargetBd, setTransferTargetBd] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  // Host Details Modal State
  const [isHostDetailsModalOpen, setIsHostDetailsModalOpen] = useState(false);
  const [selectedHostForView, setSelectedHostForView] = useState(null);
  const [hostDetailsData, setHostDetailsData] = useState(null);
  const [hostDetailsLoading, setHostDetailsLoading] = useState(false);

  // Agency Hosts & Commission Modal State
  const [isAgencyHostsModalOpen, setIsAgencyHostsModalOpen] = useState(false);
  const [selectedAgencyForHosts, setSelectedAgencyForHosts] = useState(null);
  const [agencyHostsData, setAgencyHostsData] = useState([]);
  const [agencyHostsSummary, setAgencyHostsSummary] = useState(null);
  const [agencyHostsLoading, setAgencyHostsLoading] = useState(false);

  // Super Admin Exclusive Edit Modals State
  const [isEditManagerModalOpen, setIsEditManagerModalOpen] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [editManagerForm, setEditManagerForm] = useState({ first_name: '', last_name: '', email: '', phone: '', country: 'India', is_active: true, password: '', custom_commission_pct: '' });

  const [isEditBdModalOpen, setIsEditBdModalOpen] = useState(false);
  const [editingBd, setEditingBd] = useState(null);
  const [editBdForm, setEditBdForm] = useState({ first_name: '', last_name: '', email: '', phone: '', country: 'India', manager_id: '', is_active: true, password: '', custom_commission_pct: '' });

  const [isEditAgencyModalOpen, setIsEditAgencyModalOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [editAgencyForm, setEditAgencyForm] = useState({ agency_name: '', phone: '', email: '', country: 'India', bd_user_id: '', status: 'approved', custom_agency_comm_pct: '', custom_bd_comm_pct: '', custom_manager_comm_pct: '', custom_valid_days: '' });

  const [isEditHostModalOpen, setIsEditHostModalOpen] = useState(false);
  const [editingHost, setEditingHost] = useState(null);
  const [editHostForm, setEditHostForm] = useState({ first_name: '', last_name: '', email: '', phone: '', country: 'India', agency_id: '', password: '' });

  // Super Admin Host Live Timing & Days Override Modal State
  const [isTimingModalOpen, setIsTimingModalOpen] = useState(false);
  const [timingHost, setTimingHost] = useState(null);
  const [timingForm, setTimingForm] = useState({ valid_days: 0, duration_mins: 0 });
  const [timingSaving, setTimingSaving] = useState(false);

  // Host Reassignment Modal State on Agency Deactivation / Rejection
  const [reassignModal, setReassignModal] = useState({
    isOpen: false,
    agencyId: null,
    agencyName: '',
    newStatus: '',
    targetAgencyId: '10000000-0000-4000-8000-000000000000',
    loading: false,
    isEditForm: false
  });

  // Forms
  const [managerForm, setManagerForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    user_type: 'MANAGER',
    country: 'India'
  });

  const [bdForm, setBdForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    user_type: 'BD',
    manager_id: '',
    country: 'India'
  });

  const [agencyForm, setAgencyForm] = useState({
    agency_name: '',
    real_name: '',
    email: '',
    password: '',
    whatsapp_number: '',
    country: 'India',
    bd_user_id: ''
  });

  const [hostForm, setHostForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    country: 'India',
    agency_id: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const authHeader = { Authorization: token };

      // 1. Fetch Users to get Managers and BDs
      const [usersRes, agenciesRes, hostsRes] = await Promise.all([
        usersAPI.getUsers().catch((err) => {
          console.error("Error fetching users:", err);
          return { data: [] };
        }),
        axios.get(`${constant.API_BASE_URL}/agencies`, { headers: authHeader })
          .catch((err) => {
            console.warn("Direct agencies fetch failed, trying agenciesAPI:", err);
            return agenciesAPI.getAgencies().catch((apiErr) => {
              console.error("agenciesAPI also failed:", apiErr);
              return { data: [] };
            });
          }),
        axios.get(`${constant.API_BASE_URL}/host/center/admin/hosts-list`, { headers: authHeader }).catch((err) => {
          console.error("Error fetching hosts:", err);
          return { data: { data: [] } };
        })
      ]);

      const allUsers = usersRes.data || [];
      const managerList = allUsers.filter(u => u.user_type === 'MANAGER');
      
      let bdList = allUsers.filter(u => {
        if (u.user_type !== 'BD') return false;
        const isBanned = Boolean(u.is_ban) || (u.banned_until && new Date(u.banned_until) > new Date());
        if (u.is_active === false || isBanned) return false;
        if (u.status && u.status !== 'approved' && u.status !== 'active') return false;
        if (u.bd_invitation_status && u.bd_invitation_status !== 'accepted') return false;
        return true;
      });
      if (isManager && userinfo?.id) {
        // Manager only sees their own assigned BDs
        bdList = bdList.filter(u => u.manager_id === userinfo.id);
      }

      setManagers(managerList);
      setBds(bdList);

      const rawAgencies = agenciesRes.data;
      const parsedAgencies = Array.isArray(rawAgencies) ? rawAgencies : (Array.isArray(rawAgencies?.data) ? rawAgencies.data : []);
      const uniqueAgencies = [];
      const seenAgencies = new Set();
      for (const a of parsedAgencies) {
        if (a && a.id && !seenAgencies.has(a.id)) {
          seenAgencies.add(a.id);
          const owner = allUsers.find(u => u.id === a.created_by || String(u.xoxo_id) === String(a.agency_code) || String(u.xoxo_id) === String(a.created_by));
          uniqueAgencies.push({
            ...a,
            owner_valid_days: owner ? Number(owner.valid_days || 0) : 0,
            owner_duration_mins: owner ? Number(owner.duration_mins || 0) : 0,
            owner_user_id: owner?.id || a.created_by
          });
        }
      }
      setAgencies(uniqueAgencies);
      
      const hostList = hostsRes.data?.data || (Array.isArray(hostsRes.data) ? hostsRes.data : []);
      const uniqueHosts = [];
      const seenHosts = new Set();
      for (const h of hostList) {
        const key = h.id || h.user_id;
        if (key && !seenHosts.has(key)) {
          seenHosts.add(key);
          uniqueHosts.push(h);
        }
      }
      const agencyHostsOnly = uniqueHosts.filter(h => h.agency_name && h.agency_name.trim() !== '' && h.agency_name !== 'Independent');
      setHosts(agencyHostsOnly);

    } catch (err) {
      console.error("Error fetching organization data:", err);
      showToast("Error loading organization data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Create Manager
  const handleCreateManager = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.createUser(managerForm);
      showToast("Manager account created successfully!");
      setIsManagerModalOpen(false);
      setManagerForm({ first_name: '', last_name: '', email: '', password: '', phone: '', user_type: 'MANAGER', country: 'India' });
      fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to create manager", "error");
    }
  };

  // Create BD
  const handleCreateBd = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...bdForm };
      if (isManager && userinfo?.id) {
        payload.manager_id = userinfo.id;
      }
      await usersAPI.createUser(payload);
      showToast("BD account created successfully!");
      setIsBdModalOpen(false);
      setBdForm({ first_name: '', last_name: '', email: '', password: '', phone: '', user_type: 'BD', manager_id: '', country: 'India' });
      fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to create BD", "error");
    }
  };

  // Create Agency
  const handleCreateAgency = async (e) => {
    e.preventDefault();
    try {
      await agenciesAPI.createAgency({
        agency_name: agencyForm.agency_name,
        real_name: agencyForm.real_name,
        email: agencyForm.email,
        whatsapp_number: agencyForm.whatsapp_number,
        country: agencyForm.country,
        bd_user_id: agencyForm.bd_user_id || undefined,
        xoxo_id: agencyForm.xoxo_id ? agencyForm.xoxo_id.trim() : undefined
      });
      showToast("Agency created and registered successfully!");
      setIsAgencyModalOpen(false);
      setAgencyForm({ agency_name: '', real_name: '', email: '', password: '', whatsapp_number: '', country: 'India', bd_user_id: '', xoxo_id: '' });
      fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to create agency", "error");
    }
  };

  // Create Host
  const handleCreateHost = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.createUser({
        first_name: hostForm.first_name,
        last_name: hostForm.last_name,
        email: hostForm.email,
        password: hostForm.password,
        phone: hostForm.phone,
        country: hostForm.country,
        user_type: 'HOST',
        agency_id: hostForm.agency_id
      });
      showToast("Host created and linked to agency successfully!");
      setIsHostModalOpen(false);
      setHostForm({ first_name: '', last_name: '', email: '', password: '', phone: '', country: 'India', agency_id: '' });
      fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to create host", "error");
    }
  };

  // Open Host Details Modal
  const handleOpenHostDetails = async (host) => {
    setSelectedHostForView(host);
    setIsHostDetailsModalOpen(true);
    setHostDetailsLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/host/center/admin/hosts/${host.id}/details`, {
        headers: { Authorization: token }
      });
      if (res.data?.success) {
        setHostDetailsData(res.data.data);
      } else {
        throw new Error("Failed to load details");
      }
    } catch (err) {
      console.error("Error fetching host details:", err);
      // Fallback
      setHostDetailsData({
        host: host,
        targets: {
          current_level: host.current_level,
          achieved_coins: Number(host.achieved_coins || 0),
          host_salary_diamonds: Number(host.host_amount || 0),
          instant_salary: Number(host.valid_days || 0) >= 10
            ? Number(host.host_amount || 0)
            : Number(host.instant_amount || Math.floor(Number(host.host_amount || 0) * 0.7)),
          locked_salary: Number(host.valid_days || 0) >= 10
            ? 0
            : Number(host.locked_amount || Math.ceil(Number(host.host_amount || 0) * 0.3)),
          agency_commission_diamonds: Number(host.agency_comm_amount || 0),
          valid_days: host.valid_days || 0,
          duration_mins: host.duration_mins || 0,
          duration_hours: ((Number(host.duration_mins || 0)) / 60).toFixed(1),
          progress_percentage: Math.min(100, Math.round((Number(host.achieved_coins || 0) / 10000) * 100))
        },
        recent_sessions: []
      });
    } finally {
      setHostDetailsLoading(false);
    }
  };

  // Approve / Reject Agency (with Host Reassignment prompt if deactivating)
  const handleUpdateAgencyStatus = async (agencyId, status) => {
    if (status === 'rejected' || status === 'inactive') {
      const ag = agencies.find(a => a.id === agencyId);
      setReassignModal({
        isOpen: true,
        agencyId,
        agencyName: ag?.agency_name || 'Selected Agency',
        newStatus: status,
        targetAgencyId: '10000000-0000-4000-8000-000000000000',
        loading: false,
        isEditForm: false
      });
      return;
    }

    try {
      await agenciesAPI.updateAgencyStatus(agencyId, status);
      showToast(`Agency status updated to ${status}`);
      fetchAllData();
    } catch (err) {
      showToast("Failed to update agency status", "error");
    }
  };

  // Super Admin Exclusive Edit Handlers
  const handleOpenEditManager = (mgr) => {
    setEditingManager(mgr);
    setEditManagerForm({
      first_name: mgr.first_name || '',
      last_name: mgr.last_name || '',
      email: mgr.email || '',
      phone: mgr.phone || '',
      country: mgr.country || 'India',
      is_active: mgr.is_active !== false,
      password: '',
      custom_commission_pct: mgr.custom_commission_pct !== null && mgr.custom_commission_pct !== undefined ? mgr.custom_commission_pct : ''
    });
    setIsEditManagerModalOpen(true);
  };

  const handleSaveEditManager = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.updateUser(editingManager.id, editManagerForm);
      showToast("Manager updated successfully!");
      setIsEditManagerModalOpen(false);
      fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to update manager", "error");
    }
  };

  const handleOpenEditBd = (bd) => {
    setEditingBd(bd);
    setEditBdForm({
      first_name: bd.first_name || '',
      last_name: bd.last_name || '',
      email: bd.email || '',
      phone: bd.phone || '',
      country: bd.country || 'India',
      manager_id: bd.manager_id || '',
      is_active: bd.is_active !== false,
      password: '',
      custom_commission_pct: bd.custom_commission_pct !== null && bd.custom_commission_pct !== undefined ? bd.custom_commission_pct : ''
    });
    setIsEditBdModalOpen(true);
  };

  const handleSaveEditBd = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.updateUser(editingBd.id, editBdForm);
      showToast("BD updated successfully!");
      setIsEditBdModalOpen(false);
      fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to update BD", "error");
    }
  };

  const handleOpenEditAgency = (agency) => {
    setEditingAgency(agency);
    setEditAgencyForm({
      agency_name: agency.agency_name || '',
      phone: agency.phone || agency.whatsapp_number || '',
      email: agency.email || '',
      country: agency.country || 'India',
      bd_user_id: agency.bd_user_id || '',
      status: agency.status || 'approved',
      custom_agency_comm_pct: agency.custom_agency_comm_pct !== null && agency.custom_agency_comm_pct !== undefined ? agency.custom_agency_comm_pct : '',
      custom_bd_comm_pct: agency.custom_bd_comm_pct !== null && agency.custom_bd_comm_pct !== undefined ? agency.custom_bd_comm_pct : '',
      custom_manager_comm_pct: agency.custom_manager_comm_pct !== null && agency.custom_manager_comm_pct !== undefined ? agency.custom_manager_comm_pct : '',
      custom_valid_days: agency.custom_valid_days !== null && agency.custom_valid_days !== undefined ? agency.custom_valid_days : '',
      order_no: agency.order_no !== null && agency.order_no !== undefined ? agency.order_no : ''
    });
    setIsEditAgencyModalOpen(true);
  };

  const handleSaveEditAgency = async (e) => {
    e.preventDefault();
    if (editAgencyForm.status === 'rejected' || editAgencyForm.status === 'inactive') {
      setReassignModal({
        isOpen: true,
        agencyId: editingAgency.id,
        agencyName: editingAgency.agency_name || 'Selected Agency',
        newStatus: editAgencyForm.status,
        targetAgencyId: '10000000-0000-4000-8000-000000000000',
        loading: false,
        isEditForm: true
      });
      return;
    }

    try {
      await agenciesAPI.updateAgency(editingAgency.id, editAgencyForm);
      showToast("Agency updated successfully!");
      setIsEditAgencyModalOpen(false);
      fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to update agency", "error");
    }
  };

  const handleConfirmReassignStatus = async () => {
    setReassignModal(prev => ({ ...prev, loading: true }));
    try {
      if (reassignModal.isEditForm) {
        await agenciesAPI.updateAgency(reassignModal.agencyId, {
          ...editAgencyForm,
          status: reassignModal.newStatus,
          target_agency_id: reassignModal.targetAgencyId
        });
        setIsEditAgencyModalOpen(false);
      } else {
        await agenciesAPI.updateAgencyStatus(
          reassignModal.agencyId,
          reassignModal.newStatus,
          reassignModal.targetAgencyId
        );
      }
      setReassignModal({ isOpen: false, agencyId: null, agencyName: '', newStatus: '', targetAgencyId: '10000000-0000-4000-8000-000000000000', loading: false, isEditForm: false });
      showToast("Agency status updated & hosts reassigned successfully!");
      fetchAllData();
    } catch (err) {
      console.error("Error updating agency status with host reassignment:", err);
      showToast(err.response?.data?.message || err.message || "Failed to update agency status", "error");
      setReassignModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleOpenEditHost = (host) => {
    setEditingHost(host);
    setEditHostForm({
      first_name: host.first_name || '',
      last_name: host.last_name || '',
      email: host.email || '',
      phone: host.phone || '',
      country: host.country || 'India',
      agency_id: host.agency_id || '',
      password: ''
    });
    setIsEditHostModalOpen(true);
  };

  const handleSaveEditHost = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.updateUser(editingHost.id || editingHost.user_id, editHostForm);
      showToast("Host profile updated successfully!");
      setIsEditHostModalOpen(false);
      fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to update host", "error");
    }
  };

  const getAgencyValidDays = (agency) => {
    return Number(agency?.custom_valid_days !== undefined && agency?.custom_valid_days !== null && agency?.custom_valid_days !== '' ? agency.custom_valid_days : (agency?.owner_valid_days !== undefined ? agency.owner_valid_days : (agency?.valid_days || 0)));
  };

  const getAgencyDurationMins = (agency) => {
    return Number(agency?.owner_duration_mins !== undefined ? agency.owner_duration_mins : (agency?.duration_mins || 0));
  };

  const getBdValidDays = (bd) => {
    return Number(bd?.valid_days || 0);
  };

  const getBdDurationMins = (bd) => {
    return Number(bd?.duration_mins || 0);
  };

  const getManagerValidDays = (mgr) => {
    return Number(mgr?.valid_days || 0);
  };

  const getManagerDurationMins = (mgr) => {
    return Number(mgr?.duration_mins || 0);
  };

  const handleOpenEditTiming = (host) => {
    setTimingHost({
      ...host,
      target_type: 'Host',
      target_id: host.xoxo_id || host.id || host.user_id,
      displayName: `Host: ${host.first_name} ${host.last_name || ''} (XOXO: ${host.xoxo_id || host.id})`
    });
    setTimingForm({
      valid_days: host.valid_days || 0,
      duration_mins: host.duration_mins || 0
    });
    setIsTimingModalOpen(true);
  };

  const handleOpenEditTimingForAgency = (agency) => {
    const vDays = getAgencyValidDays(agency);
    const dMins = getAgencyDurationMins(agency);
    setTimingHost({
      ...agency,
      target_type: 'Agency Owner',
      target_id: agency.owner_user_id || agency.created_by || agency.id || agency.agency_code,
      displayName: `Agency Owner: ${agency.agency_name} (Code: ${agency.agency_code || 'N/A'})`,
      valid_days: vDays,
      duration_mins: dMins
    });
    setTimingForm({
      valid_days: vDays,
      duration_mins: dMins
    });
    setIsTimingModalOpen(true);
  };

  const handleOpenEditTimingForBd = (bd) => {
    const vDays = getBdValidDays(bd);
    const dMins = getBdDurationMins(bd);
    setTimingHost({
      ...bd,
      target_type: 'Business Developer (BD)',
      target_id: bd.id || bd.xoxo_id,
      displayName: `BD: ${bd.first_name} ${bd.last_name || ''} (XOXO: ${bd.xoxo_id || bd.id})`,
      valid_days: vDays,
      duration_mins: dMins
    });
    setTimingForm({
      valid_days: vDays,
      duration_mins: dMins
    });
    setIsTimingModalOpen(true);
  };

  const handleOpenEditTimingForManager = (mgr) => {
    const vDays = getManagerValidDays(mgr);
    const dMins = getManagerDurationMins(mgr);
    setTimingHost({
      ...mgr,
      target_type: 'Regional Manager',
      target_id: mgr.id || mgr.xoxo_id,
      displayName: `Manager: ${mgr.first_name} ${mgr.last_name || ''} (XOXO: ${mgr.xoxo_id || mgr.id})`,
      valid_days: vDays,
      duration_mins: dMins
    });
    setTimingForm({
      valid_days: vDays,
      duration_mins: dMins
    });
    setIsTimingModalOpen(true);
  };

  const handleSaveEditTiming = async (e) => {
    e.preventDefault();
    setTimingSaving(true);
    try {
      const targetId = timingHost.target_id || timingHost.id || timingHost.agency_code || timingHost.xoxo_id || timingHost.user_id;
      const res = await axios.post(`${constant.API_BASE_URL}/host/center/admin/override-active-stats`, {
        target_user_id: targetId,
        valid_days: Number(timingForm.valid_days),
        duration_mins: Number(timingForm.duration_mins)
      }, {
        headers: { Authorization: token }
      });
      if (res.data?.success) {
        showToast(res.data.message || "Streaming timing & valid days updated successfully!");
        setIsTimingModalOpen(false);
        fetchAllData();
        if (selectedAgencyForHosts) {
          handleOpenAgencyHostsModal(selectedAgencyForHosts);
        }
      } else {
        showToast(res.data?.message || "Failed to update timing", "error");
      }
    } catch (err) {
      showToast("Error updating timing: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setTimingSaving(false);
    }
  };


  // Open Transfer BD Modal
  const handleOpenTransferModal = (agency) => {
    setSelectedAgencyForTransfer(agency);
    setTransferTargetBd(agency.bd_user_id || '');
    setTransferReason('');
    setIsTransferModalOpen(true);
  };

  // Open Agency Hosts & Commission Modal
  const handleOpenAgencyHostsModal = async (agency) => {
    setSelectedAgencyForHosts(agency);
    setIsAgencyHostsModalOpen(true);
    setAgencyHostsLoading(true);
    try {
      const res = await agenciesAPI.getAgencyHosts(agency.id);
      const hostList = Array.isArray(res.data)
        ? res.data
        : (Array.isArray(res.data?.data)
          ? res.data.data
          : (Array.isArray(res.data?.hosts) ? res.data.hosts : []));
      setAgencyHostsData(hostList);
      const bdPct = res.data?.bd_comm_pct !== undefined && res.data.bd_comm_pct !== null ? Number(res.data.bd_comm_pct) : 15;
      const mgrPct = res.data?.manager_comm_pct !== undefined && res.data.manager_comm_pct !== null ? Number(res.data.manager_comm_pct) : 18;
      const totalAgencyComm = res.data?.total_agency_commission !== undefined
        ? Number(res.data.total_agency_commission)
        : hostList.reduce((acc, h) => acc + Number(h.agency_commission_diamonds || 0), 0);
      const hasAssignedBd = !!selectedAgencyForHosts?.bd_user_id;
      const totalBdComm = !hasAssignedBd ? 0 : (res.data?.total_bd_commission !== undefined
        ? Number(res.data.total_bd_commission)
        : hostList.reduce((acc, h) => acc + Number(h.bd_comm_amount !== undefined ? h.bd_comm_amount : Math.floor((Number(h.agency_commission_diamonds || 0)) * (bdPct / 100))), 0));
      const totalMgrComm = !hasAssignedBd ? 0 : (res.data?.total_manager_commission !== undefined
        ? Number(res.data.total_manager_commission)
        : hostList.reduce((acc, h) => {
            const bdAmt = Number(h.bd_comm_amount !== undefined ? h.bd_comm_amount : Math.floor((Number(h.agency_commission_diamonds || 0)) * (bdPct / 100)));
            return acc + Number(h.manager_comm_amount !== undefined ? h.manager_comm_amount : Math.floor(bdAmt * (mgrPct / 100)));
          }, 0));

      setAgencyHostsSummary({
        totalCommission: totalAgencyComm,
        totalBdCommission: totalBdComm,
        totalManagerCommission: totalMgrComm,
        bdCommPct: bdPct,
        mgrCommPct: mgrPct,
        totalCoins: res.data?.total_target_coins !== undefined
          ? Number(res.data.total_target_coins)
          : hostList.reduce((acc, h) => acc + Number(h.monthly_target_coins || 0), 0),
        totalHosts: hostList.length
      });
    } catch (err) {
      console.error("Error fetching agency hosts modal data:", err);
      setAgencyHostsData([]);
      setAgencyHostsSummary(null);
    } finally {
      setAgencyHostsLoading(false);
    }
  };

  // Submit Transfer BD
  const handleExecuteTransfer = async (e) => {
    e.preventDefault();
    if (!selectedAgencyForTransfer || !transferTargetBd) {
      showToast("Please select a target BD", "error");
      return;
    }
    setTransferLoading(true);
    try {
      await agenciesAPI.transferBD(selectedAgencyForTransfer.id, {
        to_bd_id: transferTargetBd,
        reason: transferReason
      });
      showToast("Agency BD transferred successfully!");
      setIsTransferModalOpen(false);
      fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.message || "Transfer failed", "error");
    } finally {
      setTransferLoading(false);
    }
  };

  // Filtered Lists
  const filteredManagers = useMemo(() => {
    if (!Array.isArray(managers)) return [];
    return managers.filter(m => {
      if (!m) return false;
      const q = (searchQuery || '').trim().toLowerCase();
      const matchesSearch = !q ||
        `${m.first_name || ''} ${m.last_name || ''}`.toLowerCase().includes(q) ||
        String(m.email || '').toLowerCase().includes(q) ||
        String(m.xoxo_id || '').toLowerCase().includes(q) ||
        String(m.phone || '').toLowerCase().includes(q);
      const managerCountry = String(m.country || 'India').trim().toLowerCase();
      const selectedCountry = String(countryFilter || 'ALL').trim().toLowerCase();
      const matchesCountry = selectedCountry === 'all' || managerCountry === selectedCountry;
      return matchesSearch && matchesCountry;
    });
  }, [managers, searchQuery, countryFilter]);

  const filteredBds = useMemo(() => {
    if (!Array.isArray(bds)) return [];
    return bds.filter(b => {
      if (!b) return false;
      if (selectedManagerFilter && !isBdUnderManager(b, selectedManagerFilter)) {
        return false;
      }
      const q = (searchQuery || '').trim().toLowerCase();
      const matchesSearch = !q ||
        `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase().includes(q) ||
        String(b.email || '').toLowerCase().includes(q) ||
        String(b.xoxo_id || '').toLowerCase().includes(q) ||
        String(b.user_id || '').toLowerCase().includes(q) ||
        String(b.phone || '').toLowerCase().includes(q);
      const bdCountry = String(b.country || 'India').trim().toLowerCase();
      const selectedCountry = String(countryFilter || 'ALL').trim().toLowerCase();
      const matchesCountry = selectedCountry === 'all' || bdCountry === selectedCountry;
      return matchesSearch && matchesCountry;
    });
  }, [bds, searchQuery, countryFilter, selectedManagerFilter]);

  const filteredAgencies = useMemo(() => {
    if (!Array.isArray(agencies)) return [];
    return agencies.filter(a => {
      if (!a) return false;
      if (selectedBdFilter && !isAgencyUnderBd(a, selectedBdFilter)) {
        return false;
      }
      const q = (searchQuery || '').trim().toLowerCase();
      const matchesSearch = !q ||
        String(a.agency_name || '').toLowerCase().includes(q) ||
        String(a.agency_code || '').toLowerCase().includes(q) ||
        String(a.real_name || '').toLowerCase().includes(q) ||
        String(a.creator_name || '').toLowerCase().includes(q) ||
        String(a.bd_code || '').toLowerCase().includes(q) ||
        String(a.bd_name || '').toLowerCase().includes(q) ||
        String(a.whatsapp_number || '').toLowerCase().includes(q) ||
        String(a.email || '').toLowerCase().includes(q) ||
        String(a.phone || '').toLowerCase().includes(q);
      const agencyCountry = String(a.country || 'India').trim().toLowerCase();
      const selectedCountry = String(countryFilter || 'ALL').trim().toLowerCase();
      const matchesCountry = selectedCountry === 'all' || agencyCountry === selectedCountry;
      return matchesSearch && matchesCountry;
    }).sort((a, b) => {
      const orderA = (a.order_no !== null && a.order_no !== undefined) ? Number(a.order_no) : 9999;
      const orderB = (b.order_no !== null && b.order_no !== undefined) ? Number(b.order_no) : 9999;
      if (orderA !== orderB) return orderA - orderB;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [agencies, searchQuery, countryFilter, selectedBdFilter]);

  const filteredHosts = useMemo(() => {
    if (!Array.isArray(hosts)) return [];
    return hosts.filter(h => {
      if (!h || !h.agency_name || h.agency_name === 'Independent') return false;
      const q = (searchQuery || '').trim().toLowerCase();
      const matchesSearch = !q ||
        `${h.first_name || ''} ${h.last_name || ''}`.toLowerCase().includes(q) ||
        String(h.xoxo_id || '').toLowerCase().includes(q) ||
        String(h.user_id || '').toLowerCase().includes(q) ||
        String(h.agency_name || '').toLowerCase().includes(q) ||
        String(h.agency_code || '').toLowerCase().includes(q);
      const hostCountry = String(h.country || 'India').trim().toLowerCase();
      const selectedCountry = String(countryFilter || 'ALL').trim().toLowerCase();
      const matchesCountry = selectedCountry === 'all' || hostCountry === selectedCountry;
      return matchesSearch && matchesCountry;
    });
  }, [hosts, searchQuery, countryFilter]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      
      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl border text-sm font-bold ${
          toast.type === 'error' 
            ? 'bg-rose-50 border-rose-200 text-rose-700' 
            : 'bg-emerald-50 border-emerald-200 text-emerald-700'
        }`}>
          {toast.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Building2 className="text-pink-600" size={28} />
            Organization & Hierarchy Hub
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Unified management for Managers, Business Developers (BDs), Agencies, and Hosts from a single central page.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchAllData}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200 transition-colors"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          
          {isSuperAdmin && activeTab === 'managers' && (
            <button
              onClick={() => setIsManagerModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-pink-500/20 hover:opacity-95 transition-all"
            >
              <Plus size={16} /> Add Manager
            </button>
          )}

          {(isSuperAdmin || isManager) && activeTab === 'bds' && (
            <button
              onClick={() => setIsBdModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-pink-500/20 hover:opacity-95 transition-all"
            >
              <Plus size={16} /> Add BD
            </button>
          )}

          {(isSuperAdmin || isManager) && activeTab === 'agencies' && (
            <button
              onClick={() => setIsAgencyModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-500/20 hover:opacity-95 transition-all"
            >
              <Plus size={16} /> Add Agency
            </button>
          )}

          {(isSuperAdmin || isManager) && activeTab === 'hosts' && (
            <button
              onClick={() => setIsHostModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-emerald-500/20 hover:opacity-95 transition-all"
            >
              <Plus size={16} /> Add Host
            </button>
          )}
        </div>
      </div>

      {/* Sub-Tabs Bar & Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex bg-slate-100 p-1.5 rounded-2xl gap-1.5 w-full lg:w-auto overflow-x-auto">
          {isSuperAdmin && (
            <button
              onClick={() => { setActiveTab('managers'); setSearchQuery(''); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                activeTab === 'managers'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <Shield size={16} className={activeTab === 'managers' ? 'text-purple-600' : ''} />
              Managers
              <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600 font-extrabold ml-1">
                {filteredManagers.length}
              </span>
            </button>
          )}

          <button
            onClick={() => { setActiveTab('bds'); setSearchQuery(''); setSelectedManagerFilter(null); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
              activeTab === 'bds'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <GitMerge size={16} className={activeTab === 'bds' ? 'text-pink-600' : ''} />
            Business Developers (BDs)
            <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600 font-extrabold ml-1">
              {filteredBds.length}
            </span>
          </button>

          <button
            onClick={() => { setActiveTab('agencies'); setSearchQuery(''); setSelectedBdFilter(null); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
              activeTab === 'agencies'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Building2 size={16} className={activeTab === 'agencies' ? 'text-blue-600' : ''} />
            Agencies
            <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600 font-extrabold ml-1">
              {filteredAgencies.length}
            </span>
          </button>

          <button
            onClick={() => { setActiveTab('hosts'); setSearchQuery(''); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
              activeTab === 'hosts'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Users size={16} className={activeTab === 'hosts' ? 'text-emerald-600' : ''} />
            Hosts
            <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600 font-extrabold ml-1">
              {filteredHosts.length}
            </span>
          </button>
        </div>

        {/* Filter Controls: Country Filter & Search */}
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
          {/* Country Filter Dropdown */}
          <div className="flex items-center gap-2 bg-white px-3.5 py-1 rounded-2xl border border-slate-200 shadow-sm text-sm w-full sm:w-auto">
            <Globe size={16} className="text-pink-600 shrink-0" />
            <FilterCountrySelect
              value={countryFilter}
              onChange={(val) => setCountryFilter(val)}
              className="w-full sm:w-48"
            />
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder={`Search in ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white rounded-2xl border border-slate-200 text-sm font-medium outline-none focus:border-pink-500 transition-colors shadow-sm"
            />
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: MANAGERS */}
      {/* ========================================================================= */}
      {activeTab === 'managers' && isSuperAdmin && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Regional Managers</h2>
              <p className="text-xs text-slate-400 mt-0.5">Managers supervise BDs and oversee platform performance targets.</p>
            </div>
            <span className="text-xs font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
              {filteredManagers.length} Registered Managers
            </span>
          </div>

          {loading ? (
            <div className="p-20 text-center text-slate-400">Loading managers...</div>
          ) : filteredManagers.length === 0 ? (
            <div className="p-20 text-center text-slate-400">No managers found matching search criteria.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                    <th className="p-5">Manager</th>
                    <th className="p-5">Email & Contact</th>
                    <th className="p-5">Country</th>
                    <th className="p-5">Supervised BDs</th>
                    <th className="p-5 text-right text-purple-700">Manager Commission</th>
                    <th className="p-5 text-center">Valid Days</th>
                    <th className="p-5">Status</th>
                    <th className="p-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredManagers.map((mgr) => {
                    const supervisedBds = bds.filter(b => isBdUnderManager(b, mgr));
                    const supervisedAgencies = agencies.filter(a => supervisedBds.some(b => isAgencyUnderBd(a, b)));
                    const mgrRate = Number(mgr.custom_commission_pct !== null && mgr.custom_commission_pct !== undefined && mgr.custom_commission_pct !== '' ? mgr.custom_commission_pct : 18);
                    const totalManagerComm = supervisedAgencies.reduce((acc, a) => {
                      const assignedBd = bds.find(b => isAgencyUnderBd(a, b));
                      const commDiamonds = Number(a.total_agency_commission_diamonds || 0);
                      const bdRate = Number(assignedBd?.custom_commission_pct || a.custom_bd_comm_pct || a.bd_comm_pct || 15);
                      const effectiveMgrRate = Number(mgr.custom_commission_pct || a.custom_manager_comm_pct || a.manager_comm_pct || mgrRate);
                      const bdComm = Math.floor(commDiamonds * (bdRate / 100));
                      const mgrComm = Number(a.total_manager_commission_diamonds !== undefined && !mgr.custom_commission_pct && !a.custom_manager_comm_pct ? a.total_manager_commission_diamonds : Math.floor(bdComm * (effectiveMgrRate / 100)));
                      return acc + mgrComm;
                    }, 0);

                    return (
                      <tr key={mgr.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-base">
                              {mgr.first_name?.[0] || 'M'}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900">{mgr.first_name} {mgr.last_name}</div>
                              <div className="text-xs font-semibold text-slate-400">ID: {mgr.xoxo_id || mgr.user_id || mgr.id.substring(0, 8)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className="font-semibold text-slate-700">{mgr.email}</div>
                          <div className="text-xs text-slate-400">{mgr.phone || 'No Phone'}</div>
                        </td>
                        <td className="p-5">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                            {getFlag(mgr.country || 'India')} {mgr.country || 'India'}
                          </span>
                        </td>
                        <td className="p-5">
                          <button
                            onClick={() => {
                              setSelectedManagerFilter(mgr);
                              setActiveTab('bds');
                              setSearchQuery('');
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-100 transition-colors cursor-pointer"
                            title="Click to view supervised BDs"
                          >
                            <GitMerge size={12} /> {supervisedBds.length} BDs Assigned
                          </button>
                        </td>
                        <td className="p-5 text-right">
                          <div className="flex flex-col items-end">
                            <span className="inline-flex items-center gap-1 text-sm font-black text-purple-600">
                              💎 {totalManagerComm.toLocaleString()}
                            </span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${mgr.custom_commission_pct ? 'bg-purple-100 text-purple-700 border border-purple-200' : 'text-purple-500'}`}>
                              {mgr.custom_commission_pct ? `Custom: ${mgr.custom_commission_pct}%` : `Rate: ${mgrRate}%`}
                            </span>
                          </div>
                        </td>
                        <td className="p-5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black ${
                            getManagerValidDays(mgr) >= 10 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                          }`}>
                            <Clock size={11} /> {getManagerValidDays(mgr)} / 10 Days
                          </span>
                        </td>
                        <td className="p-5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-extrabold ${
                            mgr.is_active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                          }`}>
                            {mgr.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setSelectedManagerFilter(mgr);
                                setActiveTab('bds');
                                setSearchQuery('');
                              }}
                              className="px-3 py-1.5 bg-slate-100 hover:bg-purple-100 text-slate-700 hover:text-purple-700 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                            >
                              View BDs
                            </button>
                            <button
                              onClick={() => handleOpenEditTimingForManager(mgr)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-xs font-bold shadow-md shadow-indigo-500/20 hover:opacity-95 transition-all cursor-pointer whitespace-nowrap"
                              title="Edit Streaming Days & Timing for this Manager"
                            >
                              <Clock size={12} /> Edit Days
                            </button>
                            {isSuperAdmin && (
                              <button
                                onClick={() => handleOpenEditManager(mgr)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                                title="Edit Manager Profile"
                              >
                                <Edit2 size={13} /> Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: BUSINESS DEVELOPERS (BDs) */}
      {/* ========================================================================= */}
      {activeTab === 'bds' && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Business Developers (BDs)</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {isManager ? "Viewing only BDs assigned under your supervision." : "BDs recruit and manage agencies across platform territories."}
              </p>
            </div>
            <span className="text-xs font-bold text-pink-600 bg-pink-50 px-3 py-1 rounded-full">
              {filteredBds.length} BDs
            </span>
          </div>

          {selectedManagerFilter && (
            <div className="mx-6 mt-4 p-4 bg-purple-50/80 border border-purple-200 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-purple-900 animate-in fade-in">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-200 text-purple-700 flex items-center justify-center font-bold">
                  <Filter size={18} />
                </div>
                <div>
                  <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Filtered by Manager</div>
                  <div className="text-sm font-bold text-purple-950">
                    Showing BDs supervised by <span className="underline decoration-purple-400 font-extrabold">{selectedManagerFilter.first_name} {selectedManagerFilter.last_name}</span> (ID: {selectedManagerFilter.xoxo_id || selectedManagerFilter.user_id || 'N/A'})
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-purple-200 text-purple-900 rounded-full text-xs font-extrabold">
                  {filteredBds.length} BD{filteredBds.length === 1 ? '' : 's'} Assigned
                </span>
                <button
                  onClick={() => setSelectedManagerFilter(null)}
                  className="px-3.5 py-1.5 bg-white hover:bg-purple-100 text-purple-700 rounded-xl text-xs font-bold border border-purple-200 shadow-sm transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <X size={14} /> Clear Filter (Show All BDs)
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-20 text-center text-slate-400">Loading BDs...</div>
          ) : filteredBds.length === 0 ? (
            <div className="p-20 text-center text-slate-400">No BDs found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                    <th className="p-5">BD Name / ID</th>
                    <th className="p-5">Contact</th>
                    <th className="p-5">Country</th>
                    <th className="p-5">Assigned Manager</th>
                    <th className="p-5">Managed Agencies</th>
                    <th className="p-5 text-right text-pink-700">BD Commission</th>
                    <th className="p-5 text-center">Valid Days</th>
                    <th className="p-5">Status</th>
                    <th className="p-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredBds.map((bd) => {
                    const assignedManager = managers.find(m => isBdUnderManager(bd, m));
                    const managedAgencies = agencies.filter(a => isAgencyUnderBd(a, bd));
                    const bdRate = Number(bd.custom_commission_pct !== null && bd.custom_commission_pct !== undefined && bd.custom_commission_pct !== '' ? bd.custom_commission_pct : 15);
                    const totalBdComm = managedAgencies.reduce((acc, a) => {
                      const commDiamonds = Number(a.total_agency_commission_diamonds || 0);
                      const effectiveBdRate = Number(bd.custom_commission_pct || a.custom_bd_comm_pct || a.bd_comm_pct || bdRate);
                      const comm = Number(a.total_bd_commission_diamonds !== undefined && !bd.custom_commission_pct && !a.custom_bd_comm_pct ? a.total_bd_commission_diamonds : Math.floor(commDiamonds * (effectiveBdRate / 100)));
                      return acc + comm;
                    }, 0);

                    return (
                      <tr key={bd.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-pink-100 text-pink-700 font-bold flex items-center justify-center text-base">
                              {bd.first_name?.[0] || 'B'}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900">{bd.first_name} {bd.last_name}</div>
                              <div className="text-xs font-bold text-pink-600">ID: {bd.xoxo_id || bd.user_id || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className="font-semibold text-slate-700">{bd.email}</div>
                          <div className="text-xs text-slate-400">{bd.phone || 'No phone'}</div>
                        </td>
                        <td className="p-5">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                            {getFlag(bd.country || 'India')} {bd.country || 'India'}
                          </span>
                        </td>
                        <td className="p-5">
                          {assignedManager ? (
                            <button
                              onClick={() => setSelectedManagerFilter(assignedManager)}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-100 transition-colors cursor-pointer"
                              title="Filter by this manager"
                            >
                              <Shield size={12} /> {assignedManager.first_name} {assignedManager.last_name}
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Direct (No Manager)</span>
                          )}
                        </td>
                        <td className="p-5">
                          <button
                            onClick={() => {
                              setSelectedBdFilter(bd);
                              setActiveTab('agencies');
                              setSearchQuery('');
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-100 transition-colors cursor-pointer"
                            title="Click to view managed agencies"
                          >
                            <Building2 size={12} /> {managedAgencies.length} Agencies
                          </button>
                        </td>
                        <td className="p-5 text-right">
                          <div className="flex flex-col items-end">
                            <span className="inline-flex items-center gap-1 text-sm font-black text-pink-600">
                              💎 {totalBdComm.toLocaleString()}
                            </span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${bd.custom_commission_pct ? 'bg-pink-100 text-pink-700 border border-pink-200' : 'text-pink-500'}`}>
                              {bd.custom_commission_pct ? `Custom: ${bd.custom_commission_pct}%` : `Rate: ${bdRate}%`}
                            </span>
                          </div>
                        </td>
                        <td className="p-5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black ${
                            getBdValidDays(bd) >= 10 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                          }`}>
                            <Clock size={11} /> {getBdValidDays(bd)} / 10 Days
                          </span>
                        </td>
                        <td className="p-5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-extrabold ${
                            bd.is_active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                          }`}>
                            {bd.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setSelectedBdFilter(bd);
                                setActiveTab('agencies');
                                setSearchQuery('');
                              }}
                              className="px-3 py-1.5 bg-slate-100 hover:bg-blue-100 text-slate-700 hover:text-blue-700 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                            >
                              View Agencies
                            </button>
                            <button
                              onClick={() => handleOpenEditTimingForBd(bd)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-xs font-bold shadow-md shadow-indigo-500/20 hover:opacity-95 transition-all cursor-pointer whitespace-nowrap"
                              title="Edit Streaming Days & Timing for this BD"
                            >
                              <Clock size={12} /> Edit Days
                            </button>
                            {isSuperAdmin && (
                              <button
                                onClick={() => handleOpenEditBd(bd)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-pink-50 hover:bg-pink-100 text-pink-700 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                                title="Edit BD Profile"
                              >
                                <Edit2 size={13} /> Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: AGENCIES */}
      {/* ========================================================================= */}
      {activeTab === 'agencies' && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Agencies Directory</h2>
              <p className="text-xs text-slate-400 mt-0.5">Approve, monitor, and reassign agencies to Business Developers (BD Transfer).</p>
            </div>
            <span className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
              {filteredAgencies.length} Agencies
            </span>
          </div>

          {selectedBdFilter && (
            <div className="mx-6 mt-4 p-4 bg-blue-50/80 border border-blue-200 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-blue-900 animate-in fade-in">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-200 text-blue-700 flex items-center justify-center font-bold">
                  <Filter size={18} />
                </div>
                <div>
                  <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Filtered by BD</div>
                  <div className="text-sm font-bold text-blue-950">
                    Showing Agencies managed by <span className="underline decoration-blue-400 font-extrabold">{selectedBdFilter.first_name} {selectedBdFilter.last_name}</span> (ID: {selectedBdFilter.xoxo_id || selectedBdFilter.user_id || 'N/A'})
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-blue-200 text-blue-900 rounded-full text-xs font-extrabold">
                  {filteredAgencies.length} Agenc{filteredAgencies.length === 1 ? 'y' : 'ies'} Managed
                </span>
                <button
                  onClick={() => setSelectedBdFilter(null)}
                  className="px-3.5 py-1.5 bg-white hover:bg-blue-100 text-blue-700 rounded-xl text-xs font-bold border border-blue-200 shadow-sm transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <X size={14} /> Clear Filter (Show All Agencies)
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-20 text-center text-slate-400">Loading agencies...</div>
          ) : filteredAgencies.length === 0 ? (
            <div className="p-20 text-center text-slate-400">No agencies found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                    <th className="p-5 text-center w-24">Order #</th>
                    <th className="p-5">Agency / Code</th>
                    <th className="p-5">Owner / Contact</th>
                    <th className="p-5">Country</th>
                    <th className="p-5">Assigned BD</th>
                    <th className="p-5 text-center">Hosts</th>
                    <th className="p-4 text-right text-emerald-700">Agency Commission</th>
                    <th className="p-4 text-right text-pink-700">BD Commission</th>
                    <th className="p-4 text-right text-purple-700">Manager Commission</th>
                    <th className="p-5 text-center">Valid Days</th>
                    <th className="p-5">Status</th>
                    <th className="p-5">Registered On</th>
                    <th className="p-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredAgencies.map((agency) => {
                    const assignedBd = bds.find(b => isAgencyUnderBd(agency, b));
                    const assignedManager = assignedBd ? managers.find(m => isBdUnderManager(assignedBd, m)) : null;

                    const commDiamonds = Number(agency.total_agency_commission_diamonds || 0);
                    const agencyRate = Number(agency.custom_agency_comm_pct !== null && agency.custom_agency_comm_pct !== undefined && agency.custom_agency_comm_pct !== '' 
                      ? agency.custom_agency_comm_pct 
                      : (agency.agency_comm_pct !== undefined ? agency.agency_comm_pct : 13));
                    const bdRate = Number(assignedBd?.custom_commission_pct !== null && assignedBd?.custom_commission_pct !== undefined && assignedBd?.custom_commission_pct !== ''
                      ? assignedBd.custom_commission_pct
                      : (agency.custom_bd_comm_pct !== null && agency.custom_bd_comm_pct !== undefined && agency.custom_bd_comm_pct !== ''
                        ? agency.custom_bd_comm_pct 
                        : (agency.bd_comm_pct !== undefined ? agency.bd_comm_pct : 15)));
                    const managerRate = Number(assignedManager?.custom_commission_pct !== null && assignedManager?.custom_commission_pct !== undefined && assignedManager?.custom_commission_pct !== ''
                      ? assignedManager.custom_commission_pct
                      : (agency.custom_manager_comm_pct !== null && agency.custom_manager_comm_pct !== undefined && agency.custom_manager_comm_pct !== ''
                        ? agency.custom_manager_comm_pct
                        : (agency.manager_comm_pct !== undefined ? agency.manager_comm_pct : 18)));

                    const hasAgencyBd = !!agency.bd_user_id;
                    const bdDiamonds = !hasAgencyBd ? 0 : Number(agency.total_bd_commission_diamonds !== undefined && !agency.custom_bd_comm_pct && !assignedBd?.custom_commission_pct
                      ? agency.total_bd_commission_diamonds 
                      : Math.floor(commDiamonds * (bdRate / 100)));

                    const managerDiamonds = (!hasAgencyBd || !assignedBd?.manager_id) ? 0 : Number(agency.total_manager_commission_diamonds !== undefined && !agency.custom_manager_comm_pct && !assignedManager?.custom_commission_pct
                      ? agency.total_manager_commission_diamonds 
                      : Math.floor(bdDiamonds * (managerRate / 100)));

                    const isOwnerAg = Boolean(
                      agency.is_owner_agency || 
                      agency.agency_code === 1000 || 
                      agency.agency_code === '1000' || 
                      agency.id === '10000000-0000-4000-8000-000000000000'
                    );

                    return (
                      <tr key={agency.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-5 text-center">
                          <input
                            type="number"
                            defaultValue={agency.order_no ?? 9999}
                            key={`${agency.id}-${agency.order_no}`}
                            onBlur={async (e) => {
                              const val = e.target.value === '' ? null : parseInt(e.target.value);
                              if (val !== agency.order_no) {
                                try {
                                  await agenciesAPI.updateAgencyOrder(agency.id, val);
                                  showToast(`Order #${val ?? 'default'} set for ${agency.agency_name}`);
                                  fetchAllData();
                                } catch (err) {
                                  showToast("Failed to update order", "error");
                                }
                              }
                            }}
                            className="w-16 px-2 py-1.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-blue-500 rounded-xl text-xs font-black text-center text-blue-600 outline-none transition-all shadow-sm"
                            title="Edit mobile app display order (Press Tab or click away to save)"
                          />
                        </td>
                        <td className="p-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 font-bold flex items-center justify-center text-base border border-blue-100">
                              {agency.agency_name?.[0] || 'A'}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900 flex items-center gap-2">
                                <span>{agency.agency_name}</span>
                                {isOwnerAg && (
                                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                                    ⚡ Auto-Approved
                                  </span>
                                )}
                              </div>
                              {agency.agency_code ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-pink-50 text-pink-600 border border-pink-100 mt-0.5">
                                  Code: {agency.agency_code}
                                </span>
                              ) : (
                                <span className="text-[11px] text-slate-400">No Agency Code</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className="font-semibold text-slate-800">{agency.real_name || agency.creator_name || 'Owner'}</div>
                          <div className="text-xs text-slate-400">{agency.whatsapp_number || agency.phone || agency.email || 'No contact'}</div>
                        </td>
                        <td className="p-5">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                            {getFlag(agency.country || 'India')} {agency.country || 'India'}
                          </span>
                        </td>
                        <td className="p-5">
                          {assignedBd ? (
                            <button
                              onClick={() => setSelectedBdFilter(assignedBd)}
                              className="flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer text-left"
                              title="Filter agencies by this BD"
                            >
                              <span className="font-bold text-slate-800">{assignedBd.first_name} {assignedBd.last_name}</span>
                              <span className="text-[11px] text-slate-400">({assignedBd.xoxo_id || 'BD'})</span>
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Unassigned BD</span>
                          )}
                        </td>
                        <td className="p-5 text-center">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-pink-50 text-pink-700 font-bold rounded-full text-xs">
                            <Users size={13} /> {agency.active_hosts_count || agency.hosts_count || 0}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex flex-col items-end">
                            <span className="inline-flex items-center gap-1 text-sm font-black text-emerald-600">
                              💎 {commDiamonds.toLocaleString()}
                            </span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${agency.custom_agency_comm_pct ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'text-emerald-500'}`}>
                              {agency.custom_agency_comm_pct ? `Custom: ${agency.custom_agency_comm_pct}%` : `Agency (${agencyRate}%)`}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex flex-col items-end">
                            {hasAgencyBd ? (
                              <>
                                <span className="inline-flex items-center gap-1 text-sm font-black text-pink-600">
                                  💎 {bdDiamonds.toLocaleString()}
                                </span>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${assignedBd?.custom_commission_pct || agency.custom_bd_comm_pct ? 'bg-pink-100 text-pink-700 border border-pink-200' : 'text-pink-500'}`}>
                                  {assignedBd?.custom_commission_pct ? `BD Custom: ${assignedBd.custom_commission_pct}%` : (agency.custom_bd_comm_pct ? `Custom: ${agency.custom_bd_comm_pct}%` : `BD (${bdRate}%)`)}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="inline-flex items-center gap-1 text-sm font-bold text-slate-400">
                                  0 💎
                                </span>
                                <span className="text-[10px] font-medium text-slate-400 italic mt-0.5">
                                  No BD Assigned
                                </span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex flex-col items-end">
                            {hasAgencyBd && assignedBd?.manager_id ? (
                              <>
                                <span className="inline-flex items-center gap-1 text-sm font-black text-purple-600">
                                  💎 {managerDiamonds.toLocaleString()}
                                </span>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${assignedManager?.custom_commission_pct || agency.custom_manager_comm_pct ? 'bg-purple-100 text-purple-700 border border-purple-200' : 'text-purple-500'}`}>
                                  {assignedManager?.custom_commission_pct ? `Mgr Custom: ${assignedManager.custom_commission_pct}%` : (agency.custom_manager_comm_pct ? `Custom: ${agency.custom_manager_comm_pct}%` : `Manager (${managerRate}%)`)}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="inline-flex items-center gap-1 text-sm font-bold text-slate-400">
                                  0 💎
                                </span>
                                <span className="text-[10px] font-medium text-slate-400 italic mt-0.5">
                                  No Manager
                                </span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="p-5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black ${
                            getAgencyValidDays(agency) >= 10 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                          }`}>
                            <Clock size={11} /> {getAgencyValidDays(agency)} / 10 Days
                          </span>
                        </td>
                        <td className="p-5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-extrabold ${
                            isOwnerAg
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                              : (agency.status === 'approved' 
                                  ? 'bg-emerald-50 text-emerald-700' 
                                  : agency.status === 'pending'
                                  ? 'bg-amber-50 text-amber-700'
                                  : 'bg-rose-50 text-rose-700')
                          }`}>
                            {isOwnerAg ? '⚡ AUTO-APPROVED' : (agency.status ? agency.status.toUpperCase() : 'PENDING')}
                          </span>
                        </td>
                        <td className="p-5 text-slate-500 text-xs">
                          {agency.created_at ? new Date(agency.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* View Hosts & Commission Button */}
                            <button
                              onClick={() => handleOpenAgencyHostsModal(agency)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-pink-50 hover:bg-pink-100 text-pink-700 rounded-lg text-xs font-bold transition-colors"
                              title="View agency hosts and commission breakdown"
                            >
                              <Eye size={13} /> Hosts & Commission
                            </button>

                            {/* Edit Days Button */}
                            <button
                              onClick={() => handleOpenEditTimingForAgency(agency)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-xs font-bold shadow-md shadow-indigo-500/20 hover:opacity-95 transition-all cursor-pointer whitespace-nowrap"
                              title="Edit Streaming Days & Timing for this Agency"
                            >
                              <Clock size={12} /> Edit Days
                            </button>

                            {/* Transfer BD Button */}
                            <button
                              onClick={() => handleOpenTransferModal(agency)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                              title="Reassign this agency to another BD"
                            >
                              <ArrowRightLeft size={13} /> Transfer BD
                            </button>

                            {/* Edit Agency Button (Super Admin Only) */}
                            {isSuperAdmin && (
                              <button
                                onClick={() => handleOpenEditAgency(agency)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                title="Edit Agency Details"
                              >
                                <Edit2 size={13} /> Edit
                              </button>
                            )}

                            {/* Deactivate Agency Button (Super Admin) */}
                            {isSuperAdmin && agency.status === 'approved' && !agency.is_owner_agency && (
                              <button
                                onClick={() => handleUpdateAgencyStatus(agency.id, 'inactive')}
                                className="flex items-center gap-1 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                title="Deactivate this agency and shift active hosts"
                              >
                                <XCircle size={13} /> Deactivate
                              </button>
                            )}

                            {/* Reactivate Agency Button (Super Admin) */}
                            {isSuperAdmin && (agency.status === 'inactive' || agency.status === 'rejected') && !agency.is_owner_agency && (
                              <button
                                onClick={() => handleUpdateAgencyStatus(agency.id, 'approved')}
                                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                title="Re-activate this agency"
                              >
                                <CheckCircle2 size={13} /> Activate
                              </button>
                            )}

                            {/* Approval buttons if pending */}
                            {agency.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleUpdateAgencyStatus(agency.id, 'approved')}
                                  className="p-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded-lg transition-colors"
                                  title="Approve Agency"
                                >
                                  <CheckCircle2 size={16} />
                                </button>
                                <button
                                  onClick={() => handleUpdateAgencyStatus(agency.id, 'rejected')}
                                  className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg transition-colors"
                                  title="Reject Agency"
                                >
                                  <XCircle size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 4: HOSTS */}
      {/* ========================================================================= */}
      {activeTab === 'hosts' && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Hosts & Streamers</h2>
              <p className="text-xs text-slate-400 mt-0.5">Platform hosts, agency codes, streaming targets, and calculated salary commissions.</p>
            </div>
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
              {filteredHosts.length} Hosts
            </span>
          </div>

          {loading ? (
            <div className="p-20 text-center text-slate-400">Loading hosts...</div>
          ) : filteredHosts.length === 0 ? (
            <div className="p-20 text-center text-slate-400">No hosts found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                    <th className="p-5">Host / XOXO ID</th>
                    <th className="p-5">Agency & Agency Code</th>
                    <th className="p-5">Country</th>
                    <th className="p-5">Achieved Coins</th>
                    <th className="p-5">Target Level</th>
                    <th className="p-5">Calculated Salary</th>
                    <th className="p-5">Agency Commission</th>
                    <th className="p-5">Valid Days / Mins</th>
                    <th className="p-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredHosts.map((host) => {
                    const hostAmt = Number(host.host_amount || 0);
                    const validDays = Number(host.valid_days || 0);
                    const instantAmt = validDays >= 10
                      ? hostAmt
                      : Number(host.instant_amount !== undefined && host.instant_amount !== null ? host.instant_amount : Math.floor(hostAmt * 0.70));
                    const lockedAmt = validDays >= 10
                      ? 0
                      : Number(host.locked_amount !== undefined && host.locked_amount !== null ? host.locked_amount : (hostAmt - instantAmt));
                    const agencyAmt = Number(host.agency_comm_amount || 0);

                    return (
                      <tr key={host.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-5">
                          <div className="flex items-center gap-3">
                            <img 
                              src={host.avatar_url || '/xoxo-logo.jpeg'} 
                              alt="Avatar" 
                              className="w-10 h-10 rounded-full object-cover border border-slate-200"
                            />
                            <div>
                              <div className="font-bold text-slate-900">{host.first_name || 'Host'} {host.last_name || ''}</div>
                              <div className="text-xs font-extrabold text-pink-600">XOXO ID: {host.xoxo_id || host.user_id || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className="font-bold text-slate-800">{host.agency_name}</div>
                          {host.agency_code && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-pink-50 text-pink-600 border border-pink-100 mt-1">
                              Code: {host.agency_code}
                            </span>
                          )}
                        </td>
                        <td className="p-5">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                            {getFlag(host.country || 'India')} {host.country || 'India'}
                          </span>
                        </td>
                        <td className="p-5 font-black text-slate-800">
                          🪙 {Number(host.achieved_coins || 0).toLocaleString()}
                        </td>
                        <td className="p-5">
                          <span className="px-3 py-1 bg-purple-50 text-purple-700 font-extrabold text-xs rounded-full border border-purple-100">
                            Level {Number(host.current_level || 0)}
                          </span>
                        </td>
                        <td className="p-5">
                          <div className="font-extrabold text-pink-600 text-base">💎 {hostAmt.toLocaleString()}</div>
                          <div className="text-[11px] font-semibold mt-0.5">
                            {validDays >= 10 ? (
                              <span className="text-emerald-600 font-bold">
                                Instant: 💎{instantAmt.toLocaleString()} • <span className="text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded border border-emerald-200">Hold: 💎0 (Unlocked)</span>
                              </span>
                            ) : (
                              <span className="text-slate-500">
                                <span className="text-emerald-600">Instant: 💎{instantAmt.toLocaleString()}</span> • <span className="text-amber-600 font-bold">Hold: 💎{lockedAmt.toLocaleString()}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-5">
                          <div className="font-bold text-blue-600">💎 {agencyAmt.toLocaleString()}</div>
                          <div className="mt-0.5">
                            <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded ${validDays >= 10 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                              {validDays >= 10 ? '100% Commission' : '70% Commission'}
                            </span>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-extrabold ${validDays >= 10 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                            <Calendar size={12} /> {validDays} / 10 Days
                          </div>
                          <div className="text-xs text-slate-400 font-medium mt-1 flex items-center gap-1">
                            <Clock size={11} className="text-blue-500" /> {host.duration_mins || 0} Mins
                          </div>
                        </td>
                        <td className="p-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenHostDetails(host)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/20 hover:opacity-95 transition-all whitespace-nowrap cursor-pointer"
                            >
                              <Eye size={13} /> View Host
                            </button>
                            <button
                              onClick={() => handleOpenEditTiming(host)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 hover:opacity-95 transition-all whitespace-nowrap cursor-pointer"
                              title="Edit Streaming Duration & Valid Days"
                            >
                              <Clock size={13} /> Edit Timing & Days
                            </button>
                            {isSuperAdmin && (
                              <button
                                onClick={() => handleOpenEditHost(host)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer"
                                title="Edit Host Profile"
                              >
                                <Edit2 size={13} /> Edit Profile
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: TRANSFER BD */}
      {/* ========================================================================= */}
      {isTransferModalOpen && selectedAgencyForTransfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <ArrowRightLeft className="text-blue-600" size={20} />
              Transfer Agency BD
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Reassign agency <span className="font-bold text-slate-800">"{selectedAgencyForTransfer.agency_name}"</span> to another Business Developer.
            </p>

            <form onSubmit={handleExecuteTransfer} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Select New BD</label>
                <select
                  value={transferTargetBd}
                  onChange={(e) => setTransferTargetBd(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:border-blue-500"
                  required
                >
                  <option value="">-- Select Target BD --</option>
                  {bds.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.first_name} {b.last_name} (ID: {b.xoxo_id || b.user_id || b.id.substring(0, 8)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Reason for Transfer</label>
                <textarea
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  placeholder="e.g. Territory reassignment, performance restructuring..."
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setIsTransferModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={transferLoading}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-600/20 transition-all disabled:opacity-50"
                >
                  {transferLoading ? "Transferring..." : "Confirm Transfer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: CREATE MANAGER */}
      {/* ========================================================================= */}
      {isManagerModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <Shield className="text-purple-600" size={20} />
              Create Regional Manager
            </h3>
            <p className="text-xs text-slate-500 mt-1">Add a new Manager account to supervise Business Developers.</p>

            <form onSubmit={handleCreateManager} className="mt-5 space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={managerForm.first_name}
                    onChange={e => setManagerForm({ ...managerForm, first_name: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Last Name</label>
                  <input
                    type="text"
                    required
                    value={managerForm.last_name}
                    onChange={e => setManagerForm({ ...managerForm, last_name: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={managerForm.email}
                  onChange={e => setManagerForm({ ...managerForm, email: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Password</label>
                <input
                  type="password"
                  required
                  value={managerForm.password}
                  onChange={e => setManagerForm({ ...managerForm, password: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Phone</label>
                <input
                  type="text"
                  value={managerForm.phone}
                  onChange={e => setManagerForm({ ...managerForm, phone: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Country</label>
                <SearchableCountrySelect
                  value={managerForm.country}
                  onChange={(val) => setManagerForm({...managerForm, country: val})}
                  className="mt-1"
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setIsManagerModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-purple-600/20 transition-all"
                >
                  Create Manager
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: CREATE BD */}
      {/* ========================================================================= */}
      {isBdModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <GitMerge className="text-pink-600" size={20} />
              Create Business Developer (BD)
            </h3>
            <p className="text-xs text-slate-500 mt-1">Add a new BD to onboard and supervise agencies.</p>

            <form onSubmit={handleCreateBd} className="mt-5 space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={bdForm.first_name}
                    onChange={e => setBdForm({ ...bdForm, first_name: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Last Name</label>
                  <input
                    type="text"
                    required
                    value={bdForm.last_name}
                    onChange={e => setBdForm({ ...bdForm, last_name: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-pink-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={bdForm.email}
                  onChange={e => setBdForm({ ...bdForm, email: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-pink-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Password</label>
                <input
                  type="password"
                  required
                  value={bdForm.password}
                  onChange={e => setBdForm({ ...bdForm, password: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-pink-500"
                />
              </div>

              {isSuperAdmin && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Assign to Manager (Optional)</label>
                  <select
                    value={bdForm.manager_id}
                    onChange={e => setBdForm({ ...bdForm, manager_id: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-pink-500 font-semibold"
                  >
                    <option value="">-- No Manager (Direct Admin) --</option>
                    {managers.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.first_name} {m.last_name} ({m.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Phone</label>
                <input
                  type="text"
                  value={bdForm.phone}
                  onChange={e => setBdForm({ ...bdForm, phone: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-pink-500"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Country</label>
                <SearchableCountrySelect
                  value={bdForm.country}
                  onChange={(val) => setBdForm({...bdForm, country: val})}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Existing User XOXO ID (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. 100234567 (Assign existing user as BD - cannot be host/agency)"
                  value={bdForm.xoxo_id || ''}
                  onChange={e => setBdForm({ ...bdForm, xoxo_id: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-pink-500"
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setIsBdModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-pink-600/20 transition-all"
                >
                  Create BD
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: CREATE AGENCY */}
      {/* ========================================================================= */}
      {isAgencyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <Building2 className="text-blue-600" size={20} />
              Register New Agency
            </h3>
            <p className="text-xs text-slate-500 mt-1">Create an active agency and assign it to a Business Developer.</p>

            <form onSubmit={handleCreateAgency} className="mt-5 space-y-3.5">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Agency Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Royal Stars Agency"
                  value={agencyForm.agency_name}
                  onChange={e => setAgencyForm({ ...agencyForm, agency_name: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Owner / Manager Full Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={agencyForm.real_name}
                  onChange={e => setAgencyForm({ ...agencyForm, real_name: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    placeholder="agency@xoxo.live"
                    value={agencyForm.email}
                    onChange={e => setAgencyForm({ ...agencyForm, email: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">WhatsApp / Phone</label>
                  <input
                    type="text"
                    required
                    placeholder="+91 9876543210"
                    value={agencyForm.whatsapp_number}
                    onChange={e => setAgencyForm({ ...agencyForm, whatsapp_number: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Country</label>
                <SearchableCountrySelect
                  value={agencyForm.country}
                  onChange={(val) => setAgencyForm({...agencyForm, country: val})}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Agency Owner XOXO ID (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. 100234567 (Link agency directly to an existing user account)"
                  value={agencyForm.xoxo_id || ''}
                  onChange={e => setAgencyForm({ ...agencyForm, xoxo_id: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Assign to Business Developer (BD)</label>
                <select
                  value={agencyForm.bd_user_id}
                  onChange={e => setAgencyForm({ ...agencyForm, bd_user_id: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 font-semibold"
                >
                  <option value="">-- Select BD (Optional) --</option>
                  {bds.filter(b => {
                    if (agencyForm.xoxo_id && (b.xoxo_id === agencyForm.xoxo_id || b.user_id === agencyForm.xoxo_id || b.id === agencyForm.xoxo_id)) return false;
                    return true;
                  }).map(b => (
                    <option key={b.id} value={b.id}>
                      {b.first_name} {b.last_name} (ID: {b.xoxo_id || b.user_id || b.id.substring(0, 8)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setIsAgencyModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-600/20 transition-all"
                >
                  Register Agency
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: CREATE HOST */}
      {/* ========================================================================= */}
      {isHostModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <Users className="text-emerald-600" size={20} />
              Register New Host
            </h3>
            <p className="text-xs text-slate-500 mt-1">Create a host account linked directly to an agency.</p>

            <form onSubmit={handleCreateHost} className="mt-5 space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">First Name</label>
                  <input
                    type="text"
                    required
                    placeholder="First Name"
                    value={hostForm.first_name}
                    onChange={e => setHostForm({ ...hostForm, first_name: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Last Name</label>
                  <input
                    type="text"
                    required
                    placeholder="Last Name"
                    value={hostForm.last_name}
                    onChange={e => setHostForm({ ...hostForm, last_name: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Email</label>
                <input
                  type="email"
                  required
                  placeholder="host@xoxo.live"
                  value={hostForm.email}
                  onChange={e => setHostForm({ ...hostForm, email: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Password</label>
                <input
                  type="password"
                  required
                  placeholder="Create Password"
                  value={hostForm.password}
                  onChange={e => setHostForm({ ...hostForm, password: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Phone</label>
                  <input
                    type="text"
                    placeholder="Phone"
                    value={hostForm.phone}
                    onChange={e => setHostForm({ ...hostForm, phone: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Country</label>
                  <SearchableCountrySelect
                    value={hostForm.country}
                    onChange={(val) => setHostForm({...hostForm, country: val})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Select Agency</label>
                <select
                  required
                  value={hostForm.agency_id}
                  onChange={e => setHostForm({ ...hostForm, agency_id: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-500 font-semibold"
                >
                  <option value="">-- Select Agency --</option>
                  {agencies.filter(a => a.status === 'approved' || !a.status).map(a => (
                    <option key={a.id} value={a.id}>
                      {a.agency_name} (Code: {a.agency_code || 'N/A'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setIsHostModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-emerald-600/20 transition-all"
                >
                  Create Host
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: HOST VIEW, TARGET & STREAMING HISTORY */}
      {/* ========================================================================= */}
      {isHostDetailsModalOpen && selectedHostForView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full shadow-2xl border border-slate-100 animate-in zoom-in-95 my-8 max-h-[90vh] overflow-y-auto">
            
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-slate-100 pb-5">
              <div className="flex items-center gap-4">
                <img
                  src={selectedHostForView.avatar_url || '/xoxo-logo.jpeg'}
                  alt="Host Avatar"
                  className="w-14 h-14 rounded-2xl object-cover border-2 border-pink-100 shadow-sm"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-black text-slate-900">
                      {selectedHostForView.first_name} {selectedHostForView.last_name}
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-black bg-pink-50 text-pink-600 border border-pink-100">
                      Level {Number(hostDetailsData?.targets?.current_level ?? selectedHostForView.current_level ?? 0)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500 font-medium">
                    <span>XOXO ID: <strong className="text-slate-800">{selectedHostForView.xoxo_id || selectedHostForView.user_id || 'N/A'}</strong></span>
                    <span>•</span>
                    <span>Agency: <strong className="text-blue-600">{selectedHostForView.agency_name}</strong></span>
                    {selectedHostForView.agency_code && (
                      <span className="px-2 py-0.2 rounded bg-slate-100 font-bold text-slate-700">
                        Code: {selectedHostForView.agency_code}
                      </span>
                    )}
                    <span>•</span>
                    <span>{getFlag(selectedHostForView.country || 'India')} {selectedHostForView.country || 'India'}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsHostDetailsModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {hostDetailsLoading ? (
              <div className="py-20 text-center text-slate-400 font-medium">
                <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-pink-600" />
                Loading detailed host streaming & target stats...
              </div>
            ) : (
              <div className="mt-5 space-y-6">

                {/* 4 Summary Stat Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Card 1: Level & Coins Target */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50/40 p-4 rounded-2xl border border-purple-100">
                    <div className="flex items-center justify-between text-xs font-bold text-purple-700 mb-1">
                      <span>TARGET LEVEL PROGRESSION</span>
                      <Award size={16} />
                    </div>
                    <div className="text-2xl font-black text-slate-900">
                      Level {Number(hostDetailsData?.targets?.current_level ?? selectedHostForView.current_level ?? 0)}
                    </div>
                    <div className="text-xs text-slate-600 font-medium mt-1">
                      Achieved: <strong className="text-purple-700">🪙 {Number(hostDetailsData?.targets?.achieved_coins ?? selectedHostForView.achieved_coins ?? 0).toLocaleString()} Coins</strong>
                    </div>
                    {hostDetailsData?.targets?.next_level && (
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Next Level {hostDetailsData.targets.next_level.level_number}: {Number(hostDetailsData.targets.next_level.coins_remaining).toLocaleString()} coins remaining
                      </div>
                    )}
                  </div>

                  {/* Card 2: Calculated Diamonds Salary */}
                  <div className="bg-gradient-to-br from-pink-50 to-rose-50/40 p-4 rounded-2xl border border-pink-100">
                    <div className="flex items-center justify-between text-xs font-bold text-pink-700 mb-1">
                      <span>HOST CALCULATED SALARY</span>
                      <DollarSign size={16} />
                    </div>
                    <div className="text-2xl font-black text-pink-600">
                      💎 {Number(hostDetailsData?.targets?.host_salary_diamonds ?? selectedHostForView.host_amount ?? 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-600 font-medium mt-1 flex items-center gap-2">
                      {(Number(hostDetailsData?.targets?.valid_days ?? selectedHostForView?.valid_days ?? 0)) >= 10 ? (
                        <span className="text-emerald-600 font-bold">Instant (100%): 💎{Number(hostDetailsData?.targets?.host_salary_diamonds ?? selectedHostForView?.host_amount ?? 0).toLocaleString()} • Hold: 💎0 (Unlocked)</span>
                      ) : (
                        <>
                          <span className="text-emerald-600 font-bold">Instant (70%): 💎{Number(hostDetailsData?.targets?.instant_salary ?? selectedHostForView?.instant_amount ?? Math.floor(Number(selectedHostForView?.host_amount || 0) * 0.7)).toLocaleString()}</span>
                          <span>•</span>
                          <span className="text-amber-600 font-bold">Hold (30%): 💎{Number(hostDetailsData?.targets?.locked_salary ?? selectedHostForView?.locked_amount ?? Math.ceil(Number(selectedHostForView?.host_amount || 0) * 0.3)).toLocaleString()}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Card 3: Agency Commission */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50/40 p-4 rounded-2xl border border-blue-100">
                    <div className="flex items-center justify-between text-xs font-bold text-blue-700 mb-1">
                      <span>AGENCY COMMISSION</span>
                      <Building2 size={16} />
                    </div>
                    <div className="text-2xl font-black text-blue-600">
                      💎 {Number(hostDetailsData?.targets?.agency_commission_diamonds ?? selectedHostForView.agency_comm_amount ?? 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-600 font-medium mt-1">
                      Status: <strong className={Number(hostDetailsData?.targets?.valid_days ?? selectedHostForView.valid_days ?? 0) >= 10 ? 'text-emerald-600' : 'text-amber-600'}>
                        {Number(hostDetailsData?.targets?.valid_days ?? selectedHostForView.valid_days ?? 0) >= 10 ? '100% Full Commission' : '70% Commission (Under 10 Active Days)'}
                      </strong>
                    </div>
                  </div>

                  {/* Card 4: Streaming Activity */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50/40 p-4 rounded-2xl border border-emerald-100">
                    <div className="flex items-center justify-between text-xs font-bold text-emerald-700 mb-1">
                      <span>STREAMING ACTIVITY</span>
                      <Video size={16} />
                    </div>
                    <div className="text-2xl font-black text-emerald-700">
                      {hostDetailsData?.targets?.valid_days ?? selectedHostForView.valid_days ?? 0} <span className="text-sm font-bold text-slate-500">/ 10 Active Days</span>
                    </div>
                    <div className="text-xs text-slate-600 font-medium mt-1">
                      Duration: <strong className="text-slate-800">{hostDetailsData?.targets?.duration_hours ?? ((Number(selectedHostForView.duration_mins || 0)) / 60).toFixed(1)} Hours</strong> ({hostDetailsData?.targets?.duration_mins ?? selectedHostForView.duration_mins ?? 0} Mins)
                    </div>
                  </div>
                </div>

                {/* Level Progress Bar */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80">
                  <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-2">
                    <span className="flex items-center gap-1.5">
                      <TrendingUp size={14} className="text-purple-600" />
                      Current Target Level Progression
                    </span>
                    <span className="text-purple-600 font-extrabold">
                      {hostDetailsData?.targets?.progress_percentage ?? 0}%
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(100, hostDetailsData?.targets?.progress_percentage ?? 0)}%` }}
                    />
                  </div>
                </div>

                {/* Streaming Sessions History */}
                <div>
                  <h4 className="text-sm font-black text-slate-900 mb-3 flex items-center gap-2">
                    <Clock size={16} className="text-slate-400" />
                    Live Streaming Sessions History
                  </h4>

                  {(!hostDetailsData?.recent_sessions || hostDetailsData.recent_sessions.length === 0) ? (
                    <div className="bg-slate-50 rounded-2xl p-6 text-center text-xs font-medium text-slate-400 border border-dashed border-slate-200">
                      No live streaming sessions recorded for this host in the current period.
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-2xl border border-slate-100 shadow-sm">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider">
                            <th className="p-3">Session Date</th>
                            <th className="p-3">Duration (Mins)</th>
                            <th className="p-3">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {hostDetailsData.recent_sessions.map((sess, idx) => (
                            <tr key={sess.id || idx} className="hover:bg-slate-50/50">
                              <td className="p-3 font-semibold text-slate-700">
                                {sess.created_at ? new Date(sess.created_at).toLocaleString() : 'N/A'}
                              </td>
                              <td className="p-3 font-bold text-slate-800">
                                {sess.duration_mins} Mins
                              </td>
                              <td className="p-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold ${sess.is_valid_day ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                  {sess.is_valid_day ? <CheckCircle2 size={11} /> : null}
                                  {sess.is_valid_day ? 'Valid Active Day' : 'Sub-Threshold'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Footer buttons */}
                <div className="flex items-center justify-between pt-2">
                  <button
                    onClick={() => {
                      setIsHostDetailsModalOpen(false);
                      handleOpenEditTiming(selectedHostForView);
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 hover:opacity-95 transition-all cursor-pointer"
                  >
                    <Clock size={14} /> Edit Timing & Days
                  </button>
                  <button
                    onClick={() => setIsHostDetailsModalOpen(false)}
                    className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-bold shadow-sm transition-colors cursor-pointer"
                  >
                    Close
                  </button>
                </div>

              </div>
            )}

          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: AGENCY HOSTS & COMMISSION DETAILS */}
      {/* ========================================================================= */}
      {isAgencyHostsModalOpen && selectedAgencyForHosts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 max-w-4xl w-full shadow-2xl border border-slate-100 animate-in zoom-in-95 my-8 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-slate-100 pb-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-pink-500 to-rose-600 text-white font-black text-2xl flex items-center justify-center shadow-lg shadow-pink-500/30">
                  {selectedAgencyForHosts.agency_name?.[0] || 'A'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-black text-slate-900">
                      {selectedAgencyForHosts.agency_name}
                    </h3>
                    {selectedAgencyForHosts.agency_code && (
                      <span className="px-2 py-0.5 rounded-md bg-pink-50 text-pink-600 font-bold text-xs border border-pink-100">
                        Code: {selectedAgencyForHosts.agency_code}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Owner: {selectedAgencyForHosts.real_name || selectedAgencyForHosts.creator_name || 'N/A'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsAgencyHostsModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            {agencyHostsLoading ? (
              <div className="py-20 text-center text-slate-400 font-medium">
                <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-pink-600" />
                Loading agency hosts and commission breakdown...
              </div>
            ) : (
              <div className="mt-5 space-y-6">
                {/* 5 Overview Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <div className="bg-pink-50/50 border border-pink-100 rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-pink-500 tracking-wider">Total Hosts</span>
                      <Users size={18} className="text-pink-600" />
                    </div>
                    <div className="text-xl sm:text-2xl font-black text-pink-900 mt-2">
                      {agencyHostsSummary?.totalHosts || agencyHostsData.length || 0}
                    </div>
                    <div className="text-[11px] text-pink-600 mt-1">Joined hosts</div>
                  </div>

                  <div className="bg-amber-50/50 border border-amber-100 rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-amber-600 tracking-wider">Target Coins</span>
                      <span className="text-lg">🪙</span>
                    </div>
                    <div className="text-xl sm:text-2xl font-black text-amber-900 mt-2">
                      {Number(agencyHostsSummary?.totalCoins || 0).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-amber-600 mt-1">Monthly coins</div>
                  </div>

                  <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-emerald-600 tracking-wider">Agency Comm</span>
                      <span className="text-lg">💎</span>
                    </div>
                    <div className="text-xl sm:text-2xl font-black text-emerald-900 mt-2">
                      {Number(agencyHostsSummary?.totalCommission || 0).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-emerald-600 mt-1">Agency diamonds</div>
                  </div>

                  <div className="bg-pink-50/70 border border-pink-200/80 rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-pink-600 tracking-wider">BD Comm</span>
                      <span className="text-lg">💎</span>
                    </div>
                    <div className="text-xl sm:text-2xl font-black text-pink-900 mt-2">
                      {selectedAgencyForHosts?.bd_user_id ? Number(agencyHostsSummary?.totalBdCommission || 0).toLocaleString() : '0'}
                    </div>
                    <div className="text-[11px] text-pink-600 mt-1">
                      {selectedAgencyForHosts?.bd_user_id ? `BD share (${agencyHostsSummary?.bdCommPct || 15}%)` : 'No BD Assigned'}
                    </div>
                  </div>

                  <div className="bg-purple-50/70 border border-purple-200/80 rounded-2xl p-4 col-span-2 sm:col-span-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-purple-600 tracking-wider">Manager Comm</span>
                      <span className="text-lg">💎</span>
                    </div>
                    <div className="text-xl sm:text-2xl font-black text-purple-900 mt-2">
                      {selectedAgencyForHosts?.bd_user_id ? Number(agencyHostsSummary?.totalManagerCommission || 0).toLocaleString() : '0'}
                    </div>
                    <div className="text-[11px] text-purple-600 mt-1">
                      {selectedAgencyForHosts?.bd_user_id ? `Manager share (${agencyHostsSummary?.mgrCommPct || 18}%)` : 'No Manager'}
                    </div>
                  </div>
                </div>

                {/* Hosts List Table */}
                <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
                  <div className="p-4 bg-slate-50/70 border-b border-slate-100 flex items-center justify-between">
                    <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <Users size={16} className="text-pink-600" />
                      Hosts & Performance Breakdown
                    </h4>
                    <span className="text-xs font-bold text-slate-400">
                      {agencyHostsData.length} Host{agencyHostsData.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {agencyHostsData.length === 0 ? (
                    <div className="p-10 text-center text-slate-400">
                      <Users size={32} className="mx-auto mb-2 opacity-30" />
                      <p className="font-semibold text-sm">No hosts registered in this agency yet.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100">
                            <th className="p-4">Host</th>
                            <th className="p-4">XOXO ID</th>
                            <th className="p-4 text-center">Target Level</th>
                            <th className="p-4 text-center">Target Coins</th>
                            <th className="p-4 text-center">Live Days</th>
                            <th className="p-4 text-right text-emerald-700">Agency Comm</th>
                            <th className="p-4 text-right text-pink-700">BD Comm</th>
                            <th className="p-4 text-right text-purple-700">Manager Comm</th>
                            <th className="p-4 text-center text-slate-700">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {agencyHostsData.map((h) => {
                            const validDays = Number(h.valid_days || 0);
                            const comm = Number(h.agency_commission_diamonds || 0);
                            const hostBdComm = !selectedAgencyForHosts?.bd_user_id ? 0 : Number(h.bd_comm_amount !== undefined ? h.bd_comm_amount : Math.floor(comm * 0.22));
                            const hostMgrComm = !selectedAgencyForHosts?.bd_user_id ? 0 : Number(h.manager_comm_amount !== undefined ? h.manager_comm_amount : Math.floor(hostBdComm * 0.25));
                            const hostCoins = Number(h.monthly_target_coins || 0);
                            const lvl = Number(h.current_level || 0);

                            return (
                              <tr key={h.user_id || h.id} className="hover:bg-pink-50/20 transition-colors">
                                <td className="p-4">
                                  <div className="flex items-center gap-2.5">
                                    <img
                                      src={h.avatar_url || '/xoxo-logo.jpeg'}
                                      alt="avatar"
                                      className="w-8 h-8 rounded-lg object-cover bg-slate-100"
                                      onError={(e) => { e.target.src = '/xoxo-logo.jpeg'; }}
                                    />
                                    <div>
                                      <div className="font-bold text-slate-800">{h.first_name} {h.last_name}</div>
                                      <div className="text-[10px] text-slate-400">{h.email || h.phone || '—'}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-4 font-mono font-bold text-slate-700">
                                  {h.xoxo_id || (h.user_id || '').substring(0, 8)}
                                </td>
                                <td className="p-4 text-center">
                                  <span className={`inline-flex px-2 py-0.5 rounded-md font-extrabold ${
                                    lvl > 0 ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-slate-100 text-slate-500'
                                  }`}>
                                    {lvl > 0 ? `Lvl ${lvl} ⭐` : 'No Level'}
                                  </span>
                                </td>
                                <td className="p-4 text-center font-bold text-slate-800">
                                  🪙 {hostCoins.toLocaleString()}
                                </td>
                                <td className="p-4 text-center">
                                  <span className={`inline-flex px-2 py-0.5 rounded-md font-bold ${
                                    validDays >= 10 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                                  }`}>
                                    {validDays} / 10 Days
                                  </span>
                                </td>
                                <td className="p-4 text-right">
                                  <div className="flex flex-col items-end">
                                    <span className="font-black text-emerald-600 text-sm">
                                      💎 {comm.toLocaleString()}
                                    </span>
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${
                                      validDays >= 10 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-700'
                                    }`}>
                                      {validDays >= 10 ? '100%' : '70%'} ({h.agency_comm_pct || 22}%)
                                    </span>
                                  </div>
                                </td>
                                <td className="p-4 text-right">
                                  <div className="flex flex-col items-end">
                                    <span className="font-black text-pink-600 text-sm">
                                      💎 {hostBdComm.toLocaleString()}
                                    </span>
                                    <span className="text-[9px] font-semibold text-pink-500">
                                      BD ({h.bd_comm_pct || agencyHostsSummary?.bdCommPct || 15}%)
                                    </span>
                                  </div>
                                </td>
                                <td className="p-4 text-right">
                                  <div className="flex flex-col items-end">
                                    <span className="font-black text-purple-600 text-sm">
                                      💎 {hostMgrComm.toLocaleString()}
                                    </span>
                                    <span className="text-[9px] font-semibold text-purple-500">
                                      Mgr ({h.manager_comm_pct || agencyHostsSummary?.mgrCommPct || 18}%)
                                    </span>
                                  </div>
                                </td>
                                <td className="p-4 text-center">
                                  <button
                                    onClick={() => handleOpenEditTiming(h)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 hover:opacity-95 transition-all cursor-pointer whitespace-nowrap"
                                    title="Edit Streaming Duration & Valid Days"
                                  >
                                    <Clock size={12} /> Edit Days
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-between pt-2">
                  <button
                    onClick={() => {
                      setIsAgencyHostsModalOpen(false);
                      navigate(`/agencies/${selectedAgencyForHosts.id}`);
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 bg-pink-50 hover:bg-pink-100 text-pink-700 rounded-xl text-xs font-bold transition-colors"
                  >
                    <ExternalLink size={14} /> Open Full Agency Page
                  </button>
                  <button
                    onClick={() => setIsAgencyHostsModalOpen(false)}
                    className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: TRANSFER AGENCY TO ANOTHER BD */}
      {/* ========================================================================= */}
      {isTransferModalOpen && selectedAgencyForTransfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                  <ArrowRightLeft size={20} />
                </div>
                <div>
                  <h3 className="font-black text-slate-800 text-base">Reassign BD</h3>
                  <p className="text-xs text-slate-400 font-semibold">{selectedAgencyForTransfer.agency_name}</p>
                </div>
              </div>
              <button
                onClick={() => setIsTransferModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleExecuteTransfer} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Target Business Developer (BD)</label>
                <select
                  required
                  value={transferTargetBd}
                  onChange={(e) => setTransferTargetBd(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 font-semibold"
                >
                  <option value="">-- Select Target BD --</option>
                  {bds.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.first_name} {b.last_name} (XOXO: {b.xoxo_id || b.user_id || 'BD'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Transfer Reason (Optional)</label>
                <textarea
                  rows={3}
                  placeholder="e.g. Branch restructuring, manager reassignment..."
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium outline-none focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setIsTransferModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={transferLoading}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-lg shadow-blue-600/20 transition-all flex items-center gap-1.5"
                >
                  {transferLoading ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <ArrowRightLeft size={14} />
                  )}
                  {transferLoading ? 'Transferring...' : 'Confirm Transfer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: EDIT MANAGER (SUPER ADMIN ONLY) */}
      {/* ========================================================================= */}
      {isEditManagerModalOpen && editingManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold">
                  <Shield size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">Edit Manager Profile</h3>
                  <p className="text-xs text-slate-400">ID: {editingManager.xoxo_id || editingManager.user_id || 'N/A'}</p>
                </div>
              </div>
              <button
                onClick={() => setIsEditManagerModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveEditManager} className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={editManagerForm.first_name}
                    onChange={(e) => setEditManagerForm({ ...editManagerForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editManagerForm.last_name}
                    onChange={(e) => setEditManagerForm({ ...editManagerForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={editManagerForm.email}
                  onChange={(e) => setEditManagerForm({ ...editManagerForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Phone Number</label>
                  <input
                    type="text"
                    value={editManagerForm.phone}
                    onChange={(e) => setEditManagerForm({ ...editManagerForm, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Country</label>
                  <select
                    value={editManagerForm.country}
                    onChange={(e) => setEditManagerForm({ ...editManagerForm, country: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                  >
                    {commonCountries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Custom Manager Commission % (Optional)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 18 (Default: 18%)"
                  value={editManagerForm.custom_commission_pct}
                  onChange={(e) => setEditManagerForm({ ...editManagerForm, custom_commission_pct: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">New Password (Optional)</label>
                <input
                  type="password"
                  placeholder="Leave blank to keep existing password"
                  value={editManagerForm.password}
                  onChange={(e) => setEditManagerForm({ ...editManagerForm, password: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Account Status</label>
                <select
                  value={editManagerForm.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setEditManagerForm({ ...editManagerForm, is_active: e.target.value === 'active' })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditManagerModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold shadow-lg shadow-purple-600/20 transition flex items-center gap-1.5 cursor-pointer"
                >
                  <Edit2 size={13} /> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: EDIT BD (SUPER ADMIN ONLY) */}
      {/* ========================================================================= */}
      {isEditBdModalOpen && editingBd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center font-bold">
                  <GitMerge size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">Edit Business Developer</h3>
                  <p className="text-xs text-slate-400">ID: {editingBd.xoxo_id || editingBd.user_id || 'N/A'}</p>
                </div>
              </div>
              <button
                onClick={() => setIsEditBdModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveEditBd} className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={editBdForm.first_name}
                    onChange={(e) => setEditBdForm({ ...editBdForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editBdForm.last_name}
                    onChange={(e) => setEditBdForm({ ...editBdForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-pink-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={editBdForm.email}
                  onChange={(e) => setEditBdForm({ ...editBdForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-pink-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Phone Number</label>
                  <input
                    type="text"
                    value={editBdForm.phone}
                    onChange={(e) => setEditBdForm({ ...editBdForm, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Country</label>
                  <select
                    value={editBdForm.country}
                    onChange={(e) => setEditBdForm({ ...editBdForm, country: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-pink-500"
                  >
                    {commonCountries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Assigned Manager</label>
                <select
                  value={editBdForm.manager_id}
                  onChange={(e) => setEditBdForm({ ...editBdForm, manager_id: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-pink-500"
                >
                  <option value="">Direct / No Manager</option>
                  {managers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.first_name} {m.last_name} ({m.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Custom BD Commission % (Optional)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 15 (Default: by tier slab 15%)"
                  value={editBdForm.custom_commission_pct}
                  onChange={(e) => setEditBdForm({ ...editBdForm, custom_commission_pct: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-pink-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">New Password (Optional)</label>
                <input
                  type="password"
                  placeholder="Leave blank to keep existing password"
                  value={editBdForm.password}
                  onChange={(e) => setEditBdForm({ ...editBdForm, password: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-pink-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Account Status</label>
                <select
                  value={editBdForm.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setEditBdForm({ ...editBdForm, is_active: e.target.value === 'active' })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-pink-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditBdModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-xl text-xs font-bold shadow-lg shadow-pink-600/20 transition flex items-center gap-1.5 cursor-pointer"
                >
                  <Edit2 size={13} /> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: EDIT AGENCY (SUPER ADMIN ONLY) */}
      {/* ========================================================================= */}
      {isEditAgencyModalOpen && editingAgency && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                  <Building2 size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">Edit Agency</h3>
                  <p className="text-xs text-slate-400">Code: #{editingAgency.agency_code || editingAgency.id}</p>
                </div>
              </div>
              <button
                onClick={() => setIsEditAgencyModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveEditAgency} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Agency Name</label>
                <input
                  type="text"
                  required
                  value={editAgencyForm.agency_name}
                  onChange={(e) => setEditAgencyForm({ ...editAgencyForm, agency_name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Email</label>
                  <input
                    type="email"
                    value={editAgencyForm.email}
                    onChange={(e) => setEditAgencyForm({ ...editAgencyForm, email: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">WhatsApp / Phone</label>
                  <input
                    type="text"
                    value={editAgencyForm.phone}
                    onChange={(e) => setEditAgencyForm({ ...editAgencyForm, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Country</label>
                  <select
                    value={editAgencyForm.country}
                    onChange={(e) => setEditAgencyForm({ ...editAgencyForm, country: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-blue-500"
                  >
                    {commonCountries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Status</label>
                  <select
                    value={editAgencyForm.status}
                    onChange={(e) => setEditAgencyForm({ ...editAgencyForm, status: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-blue-500"
                  >
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Assigned BD</label>
                <select
                  value={editAgencyForm.bd_user_id}
                  onChange={(e) => setEditAgencyForm({ ...editAgencyForm, bd_user_id: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-blue-500"
                >
                  <option value="">-- No BD Assigned --</option>
                  {bds.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.first_name} {b.last_name} (XOXO: {b.xoxo_id || b.user_id || 'BD'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">
                  Mobile App Order # (Display Sequence)
                </label>
                <input
                  type="number"
                  placeholder="e.g. 1, 2, 3 (Lower number shows first in mobile app)"
                  value={editAgencyForm.order_no}
                  onChange={(e) => setEditAgencyForm({ ...editAgencyForm, order_no: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-blue-500"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Controls the exact sequence in which agencies appear to users in the mobile app.
                </p>
              </div>

              {/* Custom Commission Structure & Valid Days Override */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-3">
                <div className="text-xs font-extrabold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                  <span>Custom Commission & Rules Override</span>
                  <span className="text-[10px] text-pink-600 font-bold bg-pink-50 px-2 py-0.5 rounded-full">Super Admin</span>
                </div>
                <div className="grid grid-cols-3 gap-2.5">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 mb-1">Agency %</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="e.g. 13"
                      value={editAgencyForm.custom_agency_comm_pct}
                      onChange={(e) => setEditAgencyForm({ ...editAgencyForm, custom_agency_comm_pct: e.target.value })}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 mb-1">BD %</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="e.g. 15"
                      value={editAgencyForm.custom_bd_comm_pct}
                      onChange={(e) => setEditAgencyForm({ ...editAgencyForm, custom_bd_comm_pct: e.target.value })}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 mb-1">Manager %</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="e.g. 18"
                      value={editAgencyForm.custom_manager_comm_pct}
                      onChange={(e) => setEditAgencyForm({ ...editAgencyForm, custom_manager_comm_pct: e.target.value })}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 mb-1">Custom Valid Days (Override)</label>
                  <input
                    type="number"
                    placeholder="Leave blank for automatic live stream calculation"
                    value={editAgencyForm.custom_valid_days}
                    onChange={(e) => setEditAgencyForm({ ...editAgencyForm, custom_valid_days: e.target.value })}
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-emerald-500"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">If set, overrides the required 10 days rule for full 100% payout.</p>
                </div>
              </div>

              <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditAgencyModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-lg shadow-blue-600/20 transition flex items-center gap-1.5 cursor-pointer"
                >
                  <Edit2 size={13} /> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 4: EDIT HOST PROFILE (SUPER ADMIN ONLY) */}
      {/* ========================================================================= */}
      {isEditHostModalOpen && editingHost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
                  <Users size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">Edit Host Profile</h3>
                  <p className="text-xs text-slate-400">Host ID: {editingHost.xoxo_id || editingHost.user_id || 'N/A'}</p>
                </div>
              </div>
              <button
                onClick={() => setIsEditHostModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveEditHost} className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={editHostForm.first_name}
                    onChange={(e) => setEditHostForm({ ...editHostForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editHostForm.last_name}
                    onChange={(e) => setEditHostForm({ ...editHostForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Email</label>
                <input
                  type="email"
                  value={editHostForm.email}
                  onChange={(e) => setEditHostForm({ ...editHostForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Phone</label>
                  <input
                    type="text"
                    value={editHostForm.phone}
                    onChange={(e) => setEditHostForm({ ...editHostForm, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Country</label>
                  <select
                    value={editHostForm.country}
                    onChange={(e) => setEditHostForm({ ...editHostForm, country: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-500"
                  >
                    {commonCountries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Agency Assignment</label>
                <select
                  value={editHostForm.agency_id}
                  onChange={(e) => setEditHostForm({ ...editHostForm, agency_id: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-500"
                >
                  <option value="">Independent / No Agency</option>
                  {agencies.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.agency_name} (#{a.agency_code || a.id})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">New Password (Optional)</label>
                <input
                  type="password"
                  placeholder="Leave blank to keep current password"
                  value={editHostForm.password}
                  onChange={(e) => setEditHostForm({ ...editHostForm, password: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-500"
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditHostModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-lg shadow-emerald-600/20 transition flex items-center gap-1.5 cursor-pointer"
                >
                  <Edit2 size={13} /> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 5: EDIT HOST TIMING & DAYS (SUPER ADMIN OVERRIDE) */}
      {/* ========================================================================= */}
      {isTimingModalOpen && timingHost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 animate-in zoom-in-95">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                  <Clock size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">
                    {timingHost.target_type ? `Edit ${timingHost.target_type} Timing & Days` : 'Edit Host Timing & Days'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {timingHost.displayName || `Host: ${timingHost.first_name} ${timingHost.last_name || ''} (XOXO: ${timingHost.xoxo_id || timingHost.id})`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsTimingModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveEditTiming} className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Valid Days (0 - 31)</label>
                  <input
                    type="number"
                    min="0"
                    max="31"
                    required
                    value={timingForm.valid_days}
                    onChange={(e) => setTimingForm({ ...timingForm, valid_days: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:border-indigo-500"
                  />
                  <span className="text-[10px] text-slate-400 font-medium block mt-1">Rule: 10+ days = 100% full payout</span>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-bold text-slate-600">Total Duration (Mins)</label>
                    <span className="text-[10px] font-bold text-emerald-600">
                      {((Number(timingForm.duration_mins || 0)) / 60).toFixed(1)} Hrs
                    </span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    required
                    value={timingForm.duration_mins}
                    onChange={(e) => setTimingForm({ ...timingForm, duration_mins: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:border-indigo-500"
                  />
                  <span className="text-[10px] text-slate-400 font-medium block mt-1">Minutes streamed this month</span>
                </div>
              </div>

              {Number(timingForm.valid_days) >= 10 ? (
                <div className="p-3.5 bg-emerald-50 rounded-2xl border border-emerald-200 text-xs text-emerald-800 font-medium flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-emerald-900 font-bold mb-0.5">10+ Valid Days Met (100% Full Payout)</strong>
                    Target salary (70% instant + 30% locked hold) is fully unlocked. Commissions for Agency, BD, and Manager will also be released immediately.
                  </div>
                </div>
              ) : (
                <div className="p-3.5 bg-amber-50 rounded-2xl border border-amber-200 text-xs text-amber-800 font-medium flex items-start gap-2">
                  <Clock size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-amber-900 font-bold mb-0.5">Under 10 Valid Days (Penalty Mode)</strong>
                    Host 30% bonus remains locked. Agency commission will be calculated at 70% rate instead of full 100%.
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsTimingModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={timingSaving}
                  className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-95 text-white rounded-xl text-xs font-bold shadow-lg shadow-indigo-600/20 transition flex items-center gap-1.5 cursor-pointer"
                >
                  {timingSaving ? (
                    <RefreshCw size={13} className="animate-spin" />
                  ) : (
                    <Clock size={13} />
                  )}
                  {timingSaving ? 'Saving...' : `Update ${timingHost.target_type || 'Host'} Timing & Days`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Host Reassignment Modal on Agency Inactivation / Rejection */}
      {reassignModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center font-bold text-xl">
                  ⚠️
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">Deactivate Agency & Reassign Hosts</h3>
                  <p className="text-xs text-slate-400">Action: Mark "{reassignModal.agencyName}" as {reassignModal.newStatus.toUpperCase()}</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200/60 rounded-2xl p-4 mb-6 text-sm text-amber-900">
                <p className="font-semibold mb-1">Where would you like to shift the active hosts of this agency?</p>
                <p className="text-xs text-amber-700">All active hosts will be transferred automatically without losing their ongoing streaming statistics.</p>
              </div>

              <div className="space-y-4 mb-8">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                    Target Agency:
                  </label>
                  <select
                    value={reassignModal.targetAgencyId}
                    onChange={(e) => setReassignModal(prev => ({ ...prev, targetAgencyId: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 text-slate-800 text-sm font-semibold cursor-pointer"
                  >
                    <option value="10000000-0000-4000-8000-000000000000">
                      🏛️ Owner Agency (Default System Master Agency - Universal)
                    </option>
                    {agencies
                      .filter(a => a.id !== reassignModal.agencyId && a.is_active !== false && a.status === 'approved' && !a.is_owner_agency)
                      .map(a => (
                        <option key={a.id} value={a.id}>
                          {a.agency_name} (Code: {a.agency_code || 'N/A'} - {a.country || 'Universal'})
                        </option>
                      ))}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-2">
                    Hosts transferred to the Owner Agency enter a 30-day lock period before they can transfer to another private agency.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  disabled={reassignModal.loading}
                  onClick={() => setReassignModal({ isOpen: false, agencyId: null, agencyName: '', newStatus: '', targetAgencyId: '10000000-0000-4000-8000-000000000000', loading: false, isEditForm: false })}
                  className="px-5 py-2.5 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition text-sm cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={reassignModal.loading}
                  onClick={handleConfirmReassignStatus}
                  className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl shadow-lg shadow-rose-500/30 transition text-sm flex items-center gap-2 cursor-pointer active:scale-95"
                >
                  {reassignModal.loading ? 'Processing...' : 'Confirm & Reassign Hosts'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationHub;
